import { useEffect, useState, Fragment, useCallback, useRef } from "react";
import { flushSync } from "react-dom";
import { VISOR_MODULE_BREADCRUMB_ROOT } from "@/modules/workModuleCore";
import { Link } from "react-router-dom";
import api, { detailFromAxiosError, fmtMoney, fmtNumber, formatApiError, tipoLabel, API } from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import { PageHeader } from "./layoutChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DateFieldMx from "@/components/DateFieldMx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FileDown, FileSpreadsheet, FileSearch, Loader2, CheckCircle2, AlertTriangle, ArrowRightLeft, CalendarRange, Eye, Search, X, ShieldCheck, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import DiagnoseUuidDialog from "@/components/DiagnoseUuidDialog";
import CfdiConfrontaDetailDialog from "@/components/CfdiConfrontaDetailDialog";
import {
  confrontaHasDifferences,
  MesSinDiferenciasAviso,
  ConfrontaEngineBadge,
  ExcelConfrontaDiagnostico,
  ConfrontaHuecoCruceBanner,
  pickExcelDisplayTotales,
  excelRegistroEsComplementoPago,
  sistemaIvaDesdeDesglose,
  sistemaDesgloseParaResumen,
  diffIvaContabilidadVsSistema,
  confrontaComplementoPIvaPendiente,
} from "@/lib/confrontaDiff";
import * as CF from "@/lib/confrontaLabels";
import { SatExpandTableRailCell } from "@/components/SatExpandTableRailCell";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { evaluateConfrontaPreflight, getConfrontaFlujo } from "@/lib/excelContabilidadPreflight";
import { normalizeConfrontaResponse } from "@/lib/confrontaNormalize";
import ConfrontaConsideracionesTab from "@/components/confronta/ConfrontaConsideracionesTab";
import ConfrontaDiferenciasBundleDialog from "@/components/confronta/ConfrontaDiferenciasBundleDialog";
import ConfrontaRunProgress from "@/components/confronta/ConfrontaRunProgress";
import { yieldToUi } from "@/lib/yieldToUi";
import { publishConfrontaExportProgress } from "@/lib/confrontaExportProgressBus";
import { commitBlobSave, prepareBlobSaveTarget } from "@/lib/saveBlobAs";

const MES_LABELS = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const MES_FULL = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const monthLabel = (ym) => {
  if (!ym) return "";
  const [, m] = ym.split("-");
  return `${MES_LABELS[parseInt(m,10)-1]} ${ym.slice(0,4)}`;
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Parse YYYY-MM-DD into [y, m, d]
const splitDate = (s) => {
  const [y, m, d] = (s || "").split("-").map(n => parseInt(n, 10));
  return [y, m, d];
};
const monthsInRange = (fechaDesde, fechaHasta) => {
  const [yA, mA] = splitDate(fechaDesde);
  const [yB, mB] = splitDate(fechaHasta);
  if (!yA || !mA || !yB || !mB) return 0;
  return (yB - yA) * 12 + (mB - mA) + 1;
};

// Textos por dirección (ingreso/egreso)
const TXT = {
  ingreso: {
    title: CF.TITLE_CONFRONTA_INGRESO,
    subtitle: "Cruza CFDI cobrados (PUE Ingresos + Complementos de Pago) contra el control de IVA en Excel (Contabilidad).",
    breadcrumb: [VISOR_MODULE_BREADCRUMB_ROOT, "Confronta Ingresos"],
    kpiSistema: CF.CFDI_COBRADOS_SISTEMA_SAT,
    uploadTitle: CF.CARGAR_EXCEL_CONTAB_ING,
    uploadHint: "Acepta .xls y .xlsx. El sistema detecta automáticamente si es archivo de Ingresos o de Egresos.",
    uploadHint2: "Con la plantilla de 3 hojas, las filas de Ingresos se tratan como ventas sin capturar cuenta contable. Excel Contalink: etiquetas 401-... La confronta solo cruza filas con Folio Fiscal en formato UUID del SAT; transferencias, folios numéricos o conceptos sin CFDI no suman ni marcan diferencia.",
    excelBtn: CF.BTN_CARGAR_EXCEL,
    contadorLabel: CF.FILAS_INGRESO_BD_SAT,
    clienteLabel: "Cliente",
    fechaLabel: "Fec ingreso",
    endpointConfronta: "/confronta/cobrados",
    endpointConfrontaRango: "/confronta/cobrados-rango",
    soloSistemaHint: "CFDI cobrados en el sistema (SAT) (PUE o Complemento) que no aparecen en el Excel (Contabilidad).",
    soloExcelHint: "Filas del Excel (Contabilidad) que NO existen en el sistema (SAT) (CFDI no cargado o no cobrado).",
  },
  egreso: {
    title: CF.TITLE_CONFRONTA_EGRESO,
    subtitle: "Cruza CFDI recibidos efectivamente pagados (PUE + Complementos) contra el control de IVA de gastos en Excel (Contabilidad).",
    breadcrumb: [VISOR_MODULE_BREADCRUMB_ROOT, "Confronta Egresos"],
    kpiSistema: CF.CFDI_RECIBIDOS_SISTEMA_SAT,
    uploadTitle: CF.CARGAR_EXCEL_CONTAB_EGR,
    uploadHint: "Acepta .xls y .xlsx. El sistema detecta automáticamente si es archivo de Ingresos o de Egresos.",
    uploadHint2: "Plantilla 3 hojas: filas de Egresos y Nóminas se clasifican solas. Excel Contalink: prefijos 601/701/156/603 en etiqueta. Solo se confrontan filas con Folio Fiscal en formato UUID del SAT.",
    excelBtn: CF.BTN_CARGAR_EXCEL,
    contadorLabel: CF.FILAS_GASTO_BD_SAT,
    clienteLabel: "Proveedor",
    fechaLabel: "Fec egreso",
    endpointConfronta: "/confronta/egresos",
    endpointConfrontaRango: "/confronta/egresos-rango",
    soloSistemaHint: "CFDI recibidos pagados en el sistema (SAT) (PUE o Complemento) que no aparecen en el Excel (Contabilidad) de gastos.",
    soloExcelHint: "Filas de gasto del Excel (Contabilidad) que NO existen en el sistema (SAT) (CFDI no cargado o no pagado efectivamente).",
  },
};

export default function Confronta({ direccion = "ingreso", embedMode = false }) {
  const txt = TXT[direccion];
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = usePersistedClientId(clients);
  // Default al año anterior (típicamente el ejercicio que se está trabajando,
  // p.ej. en enero-marzo se declara el ejercicio anterior). El usuario puede
  // cambiarlo. Persiste entre pantallas vía localStorage.
  const [year, setYear] = useState(() => {
    const saved = Number(localStorage.getItem("vf:selected_year") || 0);
    if (saved >= 2020 && saved <= 2099) return saved;
    const now = new Date();
    // Si estamos en enero-marzo asumimos que el contador trabaja el año pasado;
    // de lo contrario, el año en curso.
    return now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
  });
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [excelRows, setExcelRows] = useState([]);
  const [excelResumen, setExcelResumen] = useState(null);
  const [confronta, setConfronta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [runProgress, setRunProgress] = useState(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);
  const exportTickRef = useRef(null);
  const [openMonths, setOpenMonths] = useState({}); // { '2024-01': true }
  const [cfdiDetail, setCfdiDetail] = useState(null); // { item, observation }
  const [searchQuery, setSearchQuery] = useState("");
  const [diagnoseOpen, setDiagnoseOpen] = useState(false);
  const [diagnoseUuid, setDiagnoseUuid] = useState("");
  const [searchResults, setSearchResults] = useState(null); // null | array
  const [searching, setSearching] = useState(false);
  const [preflightResult, setPreflightResult] = useState(null);
  const [preflightBusy, setPreflightBusy] = useState(false);
  const [preflightValidFor, setPreflightValidFor] = useState("");
  const [confirmRunOpen, setConfirmRunOpen] = useState(false);
  const [bundleDialogOpen, setBundleDialogOpen] = useState(false);
  const confrontaResultsRef = useRef(null);

  const selectedClient = clients.find((c) => String(c.id) === String(clientId)) || null;

  const confrontaPeriodKey = `${clientId || ""}|${fechaDesde || ""}|${fechaHasta || ""}|${direccion}`;

  useEffect(() => {
    api.get("/clients")
      .then(({ data }) => setClients(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error(err);
        setClients([]);
        toast.error("Error al cargar clientes");
      });
  }, []);

  useEffect(() => {
    // Por defecto: rango = año seleccionado completo (no solo el mes actual).
    // Si se cambia year, el rango se ajusta también.
    setFechaDesde(`${year}-01-01`);
    setFechaHasta(`${year}-12-31`);
    try { localStorage.setItem("vf:selected_year", String(year)); } catch { /* ignore */ }
  }, [year]);

  // Si el usuario cambia el cliente, los resultados de un "confronta" previo ya no
  // corresponden. Limpiamos estado para evitar mostrar información de otro cliente.
  useEffect(() => {
    setConfronta(null);
    setOpenMonths({});
    setCfdiDetail(null);
    setSearchResults(null);
    setDiagnoseOpen(false);
    setDiagnoseUuid("");
  }, [clientId]);

  const loadExcelCount = useCallback(async () => {
    if (!clientId) return;
    const { data } = await api.get("/excel/list", { params: { client_id: clientId, year, direccion } });
    const items = Array.isArray(data) ? data : (data?.items || []);
    setExcelRows(items);
    setExcelResumen(Array.isArray(data) ? null : (data?.resumen || null));
  }, [clientId, year, direccion]);
  useEffect(() => {
    loadExcelCount();
  }, [loadExcelCount]);

  useEffect(() => {
    setPreflightResult(null);
    setPreflightValidFor("");
  }, [clientId, year, fechaDesde, fechaHasta, direccion]);

  useEffect(() => () => {
    if (exportTickRef.current) {
      clearInterval(exportTickRef.current);
      exportTickRef.current = null;
    }
    publishConfrontaExportProgress(null);
  }, []);

  const validateContabilidad = useCallback(async () => {
    if (!clientId) {
      toast.error("Selecciona un cliente");
      return null;
    }
    if (!fechaDesde || !fechaHasta) {
      toast.error("Selecciona el período");
      return null;
    }
    setPreflightBusy(true);
    try {
      const flujo = getConfrontaFlujo(direccion);
      const { data: diag } = await api.get("/confronta/excel-diagnostico", {
        params: {
          client_id: clientId,
          fecha_desde: fechaDesde,
          fecha_hasta: fechaHasta,
          flujo,
        },
      });
      const { data: listData } = await api.get("/excel/list", {
        params: { client_id: clientId, year, direccion: flujo },
      });
      const resumen = Array.isArray(listData) ? null : (listData?.resumen || null);
      if (!Array.isArray(listData)) {
        setExcelRows(listData?.items || []);
        setExcelResumen(resumen);
      }
      let diagFinal = diag;
      const conteoPre = diag?.conteo_flujos || {};
      const onlyOtherFlujo =
        (flujo === "ingreso"
          && Number(conteoPre.egreso ?? 0) > 0
          && Number(conteoPre.ingreso ?? 0) === 0)
        || (flujo === "egreso"
          && Number(conteoPre.ingreso ?? 0) > 0
          && Number(conteoPre.egreso ?? 0) === 0);
      // Re-sync solo si hay filas sin clasificar al flujo; no forzar intent
      // cuando el periodo solo tiene el otro flujo (gastos ≠ ingresos).
      if (
        Number(diag?.filas_en_rango ?? 0) > 0
        && Number(diag?.filas_flujo ?? 0) === 0
        && flujo !== "nomina"
        && !onlyOtherFlujo
      ) {
        try {
          const { data: rs } = await api.post("/excel/re-sync-flujo", null, {
            params: { client_id: clientId, year },
          });
          if ((rs?.updated ?? 0) > 0) {
            const { data: diag2 } = await api.get("/confronta/excel-diagnostico", {
              params: {
                client_id: clientId,
                fecha_desde: fechaDesde,
                fecha_hasta: fechaHasta,
                flujo,
              },
            });
            diagFinal = diag2;
          }
        } catch {
          /* continuar con diagnóstico original */
        }
      }
      const result = evaluateConfrontaPreflight({
        flujo,
        diagnostico: diagFinal,
        excelResumen: resumen,
        year,
        fechaDesde,
        fechaHasta,
      });
      setPreflightResult({ ...result, diagnostico: diagFinal });
      setPreflightValidFor(`${clientId}|${fechaDesde}|${fechaHasta}|${direccion}`);
      if (result.ok) {
        toast.success(
          `${result.message} Pulse «Ejecutar confronta» para ver el cruce con el SAT (XML).`,
          { duration: 10000 },
        );
      } else {
        toast.warning(result.message, { duration: 14000 });
      }
      return result;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo validar la contabilidad");
      return null;
    } finally {
      setPreflightBusy(false);
    }
  }, [clientId, year, fechaDesde, fechaHasta, direccion]);

  const executeConfronta = useCallback(async () => {
    if (!clientId) {
      toast.error("Selecciona un cliente");
      return;
    }
    if (!fechaDesde || !fechaHasta) {
      toast.error("Selecciona el período");
      return;
    }
    flushSync(() => {
      setBusy(true);
      setOpenMonths({});
      setRunProgress({ percent: 5, message: "Iniciando confronta…", current: 0, total: 1 });
    });
    await yieldToUi();
    try {
      const totalMonths = monthsInRange(fechaDesde, fechaHasta);
      if (totalMonths <= 1) {
        flushSync(() => {
          setRunProgress({ percent: 25, message: "Cruzando SAT con Excel (Contabilidad)…", current: 1, total: 1 });
        });
        await yieldToUi();
        const { data } = await api.get(txt.endpointConfronta, {
          params: {
            client_id: clientId,
            fecha_desde: fechaDesde,
            fecha_hasta: fechaHasta,
            _ts: Date.now(),
          },
        });
        setRunProgress({ percent: 95, message: "Preparando resultados…", current: 1, total: 1 });
        setConfronta({
          mode: "single",
          data: normalizeConfrontaResponse(data, "cobrados"),
          fecha_desde: fechaDesde,
          fecha_hasta: fechaHasta,
        });
      } else {
        // Un solo request de rango: el backend reutiliza el barrido SAT «anywhere»
        // una vez (antes: N llamadas en serie, una por mes). Misma lógica de cruce.
        flushSync(() => {
          setRunProgress({
            percent: 12,
            message: `Cruzando ejercicio (${totalMonths} meses)…`,
            current: 0,
            total: totalMonths,
          });
        });
        await yieldToUi();
        const { data } = await api.get(txt.endpointConfrontaRango, {
          params: {
            client_id: clientId,
            fecha_desde: fechaDesde,
            fecha_hasta: fechaHasta,
            _ts: Date.now(),
          },
          timeout: 300000,
        });
        setRunProgress({
          percent: 88,
          message: "Preparando resumen por mes…",
          current: totalMonths,
          total: totalMonths,
        });
        const monthly = (Array.isArray(data?.monthly) ? data.monthly : []).map((blk) => ({
          key: blk.key,
          label: blk.label,
          from: blk.from,
          to: blk.to,
          data: normalizeConfrontaResponse(blk.data, "cobrados"),
        }));
        setConfronta({ mode: "multi", monthly, fecha_desde: fechaDesde, fecha_hasta: fechaHasta });
      }
      setRunProgress({ percent: 100, message: "Confronta completada", current: totalMonths, total: totalMonths });
      toast.success(`Confronta ejecutada para ${fechaDesde} → ${fechaHasta}`);
      requestAnimationFrame(() => {
        confrontaResultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg =
        (typeof detail === "string" && detail) ||
        detail?.message ||
        formatApiError(detail) ||
        (e?.message === "Network Error"
          ? "No se pudo conectar con el servidor al ejecutar la confronta."
          : "Error en confronta");
      toast.error(msg, { duration: 12000 });
    } finally {
      setBusy(false);
      setRunProgress(null);
    }
  }, [clientId, fechaDesde, fechaHasta, txt.endpointConfronta, txt.endpointConfrontaRango]);

  const runConfronta = async () => {
    if (!fechaDesde || !fechaHasta) { toast.error("Selecciona el período"); return; }
    if (fechaDesde > fechaHasta) { toast.error("La fecha 'desde' no puede ser mayor a 'hasta'"); return; }
    if (preflightResult?.ok && preflightValidFor === confrontaPeriodKey && !preflightBusy) {
      await executeConfronta();
      return;
    }
    const pre = await validateContabilidad();
    if (!pre) return;
    if (!pre.ok) {
      setConfirmRunOpen(true);
      return;
    }
    await executeConfronta();
  };

  const clearExportTick = useCallback(() => {
    if (exportTickRef.current) {
      clearInterval(exportTickRef.current);
      exportTickRef.current = null;
    }
  }, []);

  const pushExportProgress = useCallback((next) => {
    setExportProgress(next);
    publishConfrontaExportProgress(next);
  }, []);

  /**
   * Exportaciones largas (Excel/PDF multiperiodo) deben mostrar la ventanita de
   * progreso de inmediato — sin esperar a que el blob vuelva o a cerrar otro diálogo.
   * El progreso se publica al hub (fuera del acordeón) para que siempre se vea.
   */
  const runExportWithProgress = useCallback(async ({ label, stepLabel = "EXPORTAR", run }) => {
    const totalMonths = Math.max(1, monthsInRange(fechaDesde, fechaHasta) || 1);
    const startedAt = Date.now();
    clearExportTick();
    const initial = {
      percent: 6,
      current: totalMonths > 1 ? 1 : 0,
      total: totalMonths,
      stepLabel,
      message:
        totalMonths > 1
          ? `Procesando información (${totalMonths} meses) para ${label}…`
          : `Generando ${label}…`,
      detail:
        totalMonths > 1
          ? `Mes 1 de ${totalMonths} · no cierre esta ventana; el archivo se descargará al terminar.`
          : "No cierre esta ventana; el archivo se descargará al terminar.",
    };
    flushSync(() => {
      setExportBusy(true);
      pushExportProgress(initial);
      try {
        document.body.dataset.vfConfrontaExport = "1";
      } catch { /* ignore */ }
    });
    await yieldToUi();

    exportTickRef.current = setInterval(() => {
      setExportProgress((prev) => {
        if (!prev) return prev;
        const pct = Number(prev.percent || 6);
        // Tras el avance “por mes” (tope ~88%) no congelar: fase final de armado.
        if (pct >= 88) {
          const nextPct = Math.min(96, pct + 0.8);
          const next = {
            ...prev,
            percent: nextPct,
            current: totalMonths,
            message: `Armando ${label}…`,
            detail:
              totalMonths > 1
                ? `Periodo de ${totalMonths} meses calculado · generando el archivo (puede tardar con muchos CFDI).`
                : "Generando el archivo final…",
          };
          publishConfrontaExportProgress(next);
          return next;
        }
        const nextPct = Math.min(88, pct + 4);
        const approxMonth =
          totalMonths > 1
            ? Math.min(totalMonths, Math.max(1, Math.ceil((nextPct / 90) * totalMonths)))
            : 1;
        const next = {
          ...prev,
          percent: nextPct,
          current: approxMonth,
          message:
            totalMonths > 1
              ? `Procesando ${label} · mes ${approxMonth} de ${totalMonths}…`
              : `Generando ${label}…`,
          detail:
            totalMonths > 1
              ? `Mes ${approxMonth} de ${totalMonths} · puede tardar varios minutos con muchos CFDI.`
              : "Puede tardar varios minutos con muchos CFDI.",
        };
        publishConfrontaExportProgress(next);
        return next;
      });
    }, 1100);

    try {
      // Mantener overlay visible un momento aunque el API responda al instante.
      await new Promise((r) => setTimeout(r, 900));
      await run();
      clearExportTick();
      const done = {
        percent: 100,
        current: totalMonths,
        total: totalMonths,
        stepLabel: "DONE",
        message: `${label} listo`,
        detail: "Descarga iniciada.",
      };
      flushSync(() => {
        pushExportProgress(done);
      });
      await yieldToUi();
      const elapsed = Date.now() - startedAt;
      if (elapsed < 2000) {
        await new Promise((r) => setTimeout(r, 2000 - elapsed));
      }
      toast.success(`${label} descargado`);
    } catch (e) {
      clearExportTick();
      throw e;
    } finally {
      clearExportTick();
      await new Promise((r) => setTimeout(r, 500));
      setExportBusy(false);
      pushExportProgress(null);
      try {
        delete document.body.dataset.vfConfrontaExport;
      } catch { /* ignore */ }
    }
  }, [clearExportTick, fechaDesde, fechaHasta, pushExportProgress]);

  const dlTableroVisual = async () => {
    if (!fechaDesde || !fechaHasta || exportBusy) return;
    try {
      await runExportWithProgress({
        label: "Excel (Contabilidad)",
        stepLabel: "EXCEL",
        run: async () => {
          const { data } = await api.get(`/reports/confronta-resumen-excel`, {
            responseType: "blob",
            timeout: 300000,
            params: { client_id: clientId, fecha_desde: fechaDesde, fecha_hasta: fechaHasta, direccion },
          });
          let blob = data;
          if (!(blob instanceof Blob)) {
            blob = new Blob([blob], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
          }
          const head = new Uint8Array(await blob.slice(0, 2).arrayBuffer());
          // XLSX = PK zip; si viene JSON de error, avisar.
          if (!(head[0] === 0x50 && head[1] === 0x4b)) {
            const msg = await detailFromAxiosError({ response: { data: blob, status: 500 } });
            throw new Error(msg || "La respuesta del servidor no es un Excel válido");
          }
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          const safeFrom = (fechaDesde || "").replaceAll("-", "");
          const safeTo = (fechaHasta || "").replaceAll("-", "");
          a.href = url;
          a.download = `Confronta_${direccion}_${safeFrom}_a_${safeTo}.xlsx`;
          a.rel = "noopener";
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 300000);
        },
      });
    } catch (e) {
      const msg = (await detailFromAxiosError(e)) || e?.message || "No se pudo descargar el Excel";
      toast.error(msg);
    }
  };

  const dlTableroVisualPDF = async () => {
    if (!fechaDesde || !fechaHasta || exportBusy) return;
    const safeFrom = (fechaDesde || "").replaceAll("-", "");
    const safeTo = (fechaHasta || "").replaceAll("-", "");
    const fname = `Confronta_Diferencias_${direccion}_${safeFrom}_a_${safeTo}.pdf`;
    let saveTarget;
    try {
      // Diálogo «Guardar como» en el mismo click (antes de la espera del API).
      saveTarget = await prepareBlobSaveTarget({
        filename: fname,
        mimeType: "application/pdf",
        extensions: [".pdf"],
        description: "PDF",
      });
    } catch (e) {
      if (e?.name === "AbortError" || e?.canceled) {
        toast.message("Descarga de PDF cancelada");
        return;
      }
      saveTarget = { mode: "download", filename: fname };
    }
    try {
      await runExportWithProgress({
        label: "PDF diferencias por mes",
        stepLabel: "PDF",
        run: async () => {
          // Mismo contenido que la pantalla: secciones «Diferencias de Enero», CFDI y datos faltantes.
          const response = await api.get(`/reports/confronta-diferencias-mes-pdf`, {
            responseType: "blob",
            timeout: 180000,
            params: { client_id: clientId, fecha_desde: fechaDesde, fecha_hasta: fechaHasta, direccion },
          });
          let blob = response.data;
          if (!(blob instanceof Blob)) {
            blob = new Blob([blob], { type: "application/pdf" });
          } else if (!blob.type || blob.type === "application/octet-stream") {
            blob = new Blob([blob], { type: "application/pdf" });
          }
          const head = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
          const looksPdf = head[0] === 0x25 && head[1] === 0x50 && head[2] === 0x44 && head[3] === 0x46;
          if (!looksPdf) {
            const msg = await detailFromAxiosError({ response: { data: blob, status: response.status } });
            throw new Error(msg || "La respuesta del servidor no es un PDF válido");
          }
          const cd = response.headers?.["content-disposition"] || response.headers?.["Content-Disposition"] || "";
          const fromHeader = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"/i.exec(cd);
          const headerName = fromHeader ? decodeURIComponent(fromHeader[1] || fromHeader[2] || "") : "";
          await commitBlobSave(saveTarget, blob, headerName || fname);
        },
      });
    } catch (e) {
      const timedOut = e?.code === "ECONNABORTED" || e?.name === "TimeoutError" || /timeout/i.test(String(e?.message || ""));
      const msg = timedOut
        ? "La generación del PDF tardó demasiado y se canceló. Intente un periodo más corto."
        : ((await detailFromAxiosError(e)) || e?.message || "No se pudo descargar el PDF");
      toast.error(msg);
    }
  };

  const dlIntegracionSaldos = async () => {
    if (!fechaDesde || !fechaHasta || exportBusy) return;
    try {
      await runExportWithProgress({
        label: "Integración de saldos",
        stepLabel: "EXCEL",
        run: async () => {
          const { data } = await api.get(`/reports/confronta-integracion-saldos-excel`, {
            responseType: "blob",
            timeout: 300000,
            params: { client_id: clientId, fecha_desde: fechaDesde, fecha_hasta: fechaHasta, direccion },
          });
          const url = URL.createObjectURL(data);
          const a = document.createElement("a");
          const safeFrom = (fechaDesde || "").replaceAll("-", "");
          const safeTo = (fechaHasta || "").replaceAll("-", "");
          a.href = url;
          a.download = `Integracion_saldos_${direccion}_${safeFrom}_a_${safeTo}.xlsx`;
          a.rel = "noopener";
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 300000);
        },
      });
    } catch (e) {
      const msg = (await detailFromAxiosError(e)) || formatApiError(e.response?.data?.detail) || "No se pudo descargar la Integración de saldos";
      toast.error(msg);
    }
  };

  const dlDiferenciasMes = async (fd, fh) => {
    const desde = fd || fechaDesde;
    const hasta = fh || fechaHasta;
    if (!desde || !hasta || exportBusy) return;
    try {
      await runExportWithProgress({
        label: "Excel de diferencias",
        stepLabel: "EXCEL",
        run: async () => {
          const { data } = await api.get("/reports/confronta-diferencias-mes-excel", {
            responseType: "blob",
            timeout: 300000,
            params: { client_id: clientId, fecha_desde: desde, fecha_hasta: hasta, direccion },
          });
          const url = URL.createObjectURL(data);
          const a = document.createElement("a");
          a.href = url;
          a.download = `Confronta_Diferencias_${direccion}_${(desde || "").replaceAll("-", "")}_a_${(hasta || "").replaceAll("-", "")}.xlsx`;
          a.rel = "noopener";
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 300000);
        },
      });
    } catch (e) {
      const msg = (await detailFromAxiosError(e)) || formatApiError(e.response?.data?.detail) || "No se pudo descargar el Excel de diferencias";
      toast.error(msg);
    }
  };

  const dlMismoMesFechas = async (fd, fh) => {
    const desde = fd || fechaDesde;
    const hasta = fh || fechaHasta;
    if (!desde || !hasta || exportBusy) return;
    try {
      await runExportWithProgress({
        label: "Excel mismo mes · fechas",
        stepLabel: "EXCEL",
        run: async () => {
          const { data } = await api.get("/reports/confronta-mismo-mes-fechas-excel", {
            responseType: "blob",
            timeout: 300000,
            params: {
              client_id: clientId,
              fecha_desde: desde,
              fecha_hasta: hasta,
              direccion,
              _ts: Date.now(),
            },
          });
          const url = URL.createObjectURL(data);
          const a = document.createElement("a");
          a.href = url;
          a.download = `Confronta_mismo_mes_fechas_${direccion}_${(desde || "").replaceAll("-", "")}_a_${(hasta || "").replaceAll("-", "")}.xlsx`;
          a.rel = "noopener";
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 300000);
        },
      });
    } catch (e) {
      const msg = (await detailFromAxiosError(e)) || formatApiError(e.response?.data?.detail) || "No se pudo descargar el reporte de fechas";
      toast.error(msg);
    }
  };



  const openCfdiDetail = async (uuid, observation, alternateClientId, excelOnlyData) => {
    // Si la fuente es solo el Excel (no hay XML cargado), mostramos directamente
    // los datos del Excel con una observación clara
    if (excelOnlyData) {
      const ed = excelOnlyData._excel_data || {};
      const obs = {
        titulo: "UUID encontrado solamente en el Excel (Contabilidad) cargado",
        detalle: `Este UUID NO tiene un XML cargado en el sistema (SAT), pero SÍ aparece en la hoja del Control de IVA en Excel (Contabilidad) que cargaste.\n\n` +
                 `Origen: Excel (Contabilidad) del cliente ${excelOnlyData._cliente_nombre || ""} (${excelOnlyData._cliente_rfc || ""})\n` +
                 `Fila Excel (Contabilidad): ${ed.tipo || "Factura"} · etiqueta "${ed.etiqueta || ""}"\n` +
                 `Fecha de ingreso: ${ed.fecha_ingreso || "—"}\n` +
                 `Folio: ${excelOnlyData.folio || "—"} · Cliente: ${excelOnlyData.nombre_receptor || ""}\n` +
                 `Método de pago: ${excelOnlyData.metodo_pago || "—"}\n\n` +
                 `Importes según el Excel (Contabilidad):\n` +
                 `• Total factura: ${ed.total_factura ? "$"+(ed.total_factura).toFixed(2) : "—"}\n` +
                 `• Subtotal cobrado: $${(ed.subtotal_cobrado||0).toFixed(2)}\n` +
                 `• IVA cobrado: $${(ed.iva_cobrado||0).toFixed(2)}\n` +
                 `• Total cobrado: $${(ed.total_cobrado||0).toFixed(2)}\n\n` +
                 `Acción sugerida: Descarga el XML correspondiente desde el SAT y cárgalo en el módulo Visor CFDI para que el sistema pueda cuadrarlo.`,
        color: "amber",
      };
      setCfdiDetail({ item: null, observation: obs, uuid });
      return;
    }
    try {
      const cid = alternateClientId || clientId;
      const { data } = await api.get("/cfdi/by-uuid", { params: { client_id: cid, uuid }});
      setCfdiDetail({ item: data, observation });
    } catch (e) {
      // Si no existe en la BD (típico de "PUE faltantes en Sistema"), aún mostramos la observación
      if (e.response?.status === 404) {
        setCfdiDetail({ item: null, observation, uuid });
      } else {
        toast.error("Error obteniendo CFDI");
      }
    }
  };

  const openInSatVisor = async (uuid, alternateClientId) => {
    const u = (uuid || "").trim();
    if (!u) return;
    await openCfdiDetail(u, null, alternateClientId);
  };

  const runSearch = async () => {
    const q = (searchQuery || "").trim();
    if (q.length < 2) { toast.error("Escribe al menos 2 caracteres (folio o UUID)"); return; }
    setSearching(true);
    try {
      const { data } = await api.get("/cfdi/buscar", { params: { q, client_id: clientId }});
      setSearchResults(data || []);
      if (!data || data.length === 0) toast.info("Sin resultados para esa búsqueda");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error buscando CFDI");
    } finally { setSearching(false); }
  };
  const clearSearch = () => { setSearchQuery(""); setSearchResults(null); };

  const years = [year+1, year, year-1, year-2, year-3, year-4];

  const headerRight = (
          <div className="flex items-center gap-2">
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="w-56 h-9 rounded-sm" data-testid="confronta-client-select"><SelectValue placeholder="Cliente"/></SelectTrigger>
              <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.razon_social} — {c.rfc}{c.owner_email ? `  ·  ${c.owner_email}` : ""}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v)=>setYear(parseInt(v))}>
              <SelectTrigger className="w-24 h-9 rounded-sm" data-testid="confronta-year-select"><SelectValue/></SelectTrigger>
              <SelectContent>{years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        );

  return (
    <>
      {/* Overlay a nivel raíz (portal a body): visible aunque el acordeón tenga stacking context. */}
      {exportBusy && exportProgress ? (
        <ConfrontaRunProgress
          progress={exportProgress}
          testId="confronta-export-progress"
        />
      ) : null}
      {busy && runProgress ? (
        <ConfrontaRunProgress
          progress={runProgress}
          testId="confronta-run-progress"
        />
      ) : null}
      {embedMode ? (
        <div className="flex flex-wrap items-center justify-end gap-2 mb-4">{headerRight}</div>
      ) : (
      <PageHeader
        title={txt.title}
        subtitle={txt.subtitle}
        breadcrumb={txt.breadcrumb}
        right={headerRight}
      />
      )}
      <div className={embedMode ? "space-y-6" : "p-8 space-y-6"}>
        {!clients.length ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-sm p-10 text-center">
            <div className="text-sm text-slate-500">Registra primero un cliente.</div>
          </div>
        ) : (
        <>
          {/* Filtros + ejecución */}
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-[11px] text-slate-600 flex items-center gap-1"><CalendarRange className="w-3 h-3"/> Desde</Label>
                <DateFieldMx
                  value={fechaDesde}
                  onChange={setFechaDesde}
                  className="rounded-sm h-9 w-[150px] mt-1"
                  minIso="2000-01-01"
                  maxIso="2100-12-31"
                  data-testid="conf-fecha-desde"
                />
              </div>
              <div>
                <Label className="text-[11px] text-slate-600 flex items-center gap-1"><CalendarRange className="w-3 h-3"/> Hasta</Label>
                <DateFieldMx
                  value={fechaHasta}
                  onChange={setFechaHasta}
                  className="rounded-sm h-9 w-[150px] mt-1"
                  minIso="2000-01-01"
                  maxIso="2100-12-31"
                  data-testid="conf-fecha-hasta"
                />
              </div>
              <Button
                onClick={validateContabilidad}
                disabled={busy || preflightBusy || !clientId}
                variant="outline"
                className="h-9 rounded-sm text-xs border-slate-300"
                data-testid="validate-contabilidad-btn"
              >
                {preflightBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : <ShieldCheck className="w-3.5 h-3.5 mr-1.5"/>}
                Validar contabilidad
              </Button>
              <Button onClick={runConfronta} disabled={busy || preflightBusy} className="h-9 rounded-sm bg-slate-900 hover:bg-slate-800 text-xs" data-testid="run-confronta-btn">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : null}
                Ejecutar confronta
              </Button>
              {clientId && fechaDesde && fechaHasta && (
                <div className="flex gap-2 flex-wrap">
                  <Button onClick={dlTableroVisual} disabled={exportBusy || busy} variant="outline" className="h-9 rounded-sm text-xs border-[#0F2D5C] text-[#0F2D5C] hover:bg-[#0F2D5C]/5" data-testid="dl-tablero-btn">
                    {exportBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5"/>} {CF.DESCARGAR_EXCEL_CONTAB}
                  </Button>
                  <Button onClick={dlIntegracionSaldos} disabled={exportBusy || busy} variant="outline" className="h-9 rounded-sm text-xs border-[#0F2D5C] text-[#0F2D5C] hover:bg-[#0F2D5C]/5" data-testid="dl-integracion-saldos-btn">
                    <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5"/> {CF.INTEGRACION_SALDOS_LABEL}
                  </Button>
                  <Button onClick={() => dlMismoMesFechas()} disabled={exportBusy || busy} variant="outline" className="h-9 rounded-sm text-xs border-amber-400 text-amber-800 hover:bg-amber-50" data-testid="dl-mismo-mes-fechas-btn">
                    <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5"/> {CF.MISMO_MES_FECHAS_EXCEL}
                  </Button>
                  <Button onClick={dlTableroVisualPDF} disabled={exportBusy || busy} variant="outline" className="h-9 rounded-sm text-xs border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="dl-tablero-pdf-btn">
                    {exportBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : <FileSearch className="w-3.5 h-3.5 mr-1.5"/>} Descargar PDF
                  </Button>
                  <Button
                    onClick={() => setBundleDialogOpen(true)}
                    disabled={exportBusy || busy}
                    variant="outline"
                    className="h-9 rounded-sm text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
                    data-testid="dl-diferencias-mes-pdf-btn"
                  >
                    <FileSearch className="w-3.5 h-3.5 mr-1.5"/> Generar diferencias y envío (PDF + Excel)
                  </Button>
                </div>
              )}
              <span
                className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200"
                title="Filas del Excel (Contabilidad) en BD para este flujo y año. El cruce SAT usa los XML cargados (cobrados/pagados en el período)."
                data-testid="confronta-excel-rows-badge"
              >
                {excelRows.length} {txt.contadorLabel} {year}
                {excelResumen?.totales?.total > 0.01 ? (
                  <> · {fmtMoney(excelResumen.totales.total)} en BD</>
                ) : null}
              </span>
              {excelRows.length === 0 && (
                <a href="/carga-informacion" className="text-[11px] text-[#0F2D5C] underline hover:text-[#3DA873]" data-testid="link-cargar-excel">
                  {CF.LINK_CARGAR_EXCEL_CONTADOR}
                </a>
              )}
              {confronta?.mode === "multi" && (
                <span className="text-[11px] text-slate-500 ml-2">
                  Período de {monthsInRange(fechaDesde, fechaHasta)} meses · clic en una fila para ver detalle
                </span>
              )}
            </div>
            {preflightResult && (
              <div
                className={`mt-2 rounded-sm border px-2.5 py-1.5 text-[11px] leading-snug ${
                  preflightResult.level === "ok"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-950"
                    : "border-amber-200 bg-amber-50 text-amber-950"
                }`}
                data-testid="confronta-preflight-banner"
              >
                {preflightResult.ok ? (
                  <>
                    <p className="m-0">
                      <span className="font-semibold">{preflightResult.title}</span>
                      {" — "}
                      {preflightResult.message}
                    </p>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="text-[10px] opacity-90 m-0">
                        Valida solo el Excel del período; ejecute la confronta para cruzar con XML SAT
                        {preflightResult.diagnostico?.sat_cobrados_uuids != null ? (
                          <>
                            {" "}
                            · UUID en sistema:{" "}
                            <strong>{preflightResult.diagnostico.sat_cobrados_uuids}</strong>
                          </>
                        ) : null}
                        .
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        className="h-7 px-2.5 text-[10px] bg-emerald-800 hover:bg-emerald-900 shrink-0"
                        disabled={busy || preflightBusy}
                        onClick={() => executeConfronta()}
                        data-testid="preflight-run-confronta-btn"
                      >
                        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                        Ejecutar confronta
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="font-semibold m-0">{preflightResult.title}</p>
                    <p className="mt-0.5 m-0">{preflightResult.message}</p>
                    {preflightResult.recomendacion && !preflightResult.wrongFlujoOnly && (
                      <p className="mt-0.5 m-0 opacity-90">{preflightResult.recomendacion}</p>
                    )}
                    <div className="mt-1.5 flex flex-col gap-1">
                    {preflightResult.canRunAnyway && (
                      <Button
                        type="button"
                        variant="outline"
                        className="h-7 text-[10px] w-fit border-amber-400"
                        disabled={busy || preflightBusy}
                        onClick={() => setConfirmRunOpen(true)}
                        data-testid="confronta-preflight-run-anyway"
                      >
                        Ejecutar confronta…
                      </Button>
                    )}
                    {preflightResult.suggestedYear && (
                      <Button
                        type="button"
                        variant="outline"
                        className="h-7 text-[10px] w-fit"
                        disabled={busy || preflightBusy}
                        onClick={() => {
                          setYear(preflightResult.suggestedYear);
                          toast.info(`Año ${preflightResult.suggestedYear}. Valide y ejecute de nuevo.`);
                        }}
                      >
                        Cambiar a año {preflightResult.suggestedYear}
                      </Button>
                    )}
                    {preflightResult.suggestedOpen && (
                      <p className="m-0">
                        <Link
                          to={`/confronta-cfdi?open=${preflightResult.suggestedOpen}`}
                          className="underline font-semibold"
                        >
                          {preflightResult.suggestedOpen === "egresos"
                            ? "Abrir Confronta Egresos"
                            : preflightResult.suggestedOpen === "nominas"
                              ? "Abrir Confronta Nóminas"
                              : "Abrir Confronta Ingresos"}
                        </Link>
                        {preflightResult.wrongFlujoOnly ? "." : null}
                      </p>
                    )}
                    {!preflightResult.wrongFlujoOnly && (
                      <p className="m-0">
                        <Link to="/carga-informacion" className="underline font-semibold">
                          Carga de información
                        </Link>
                        {" "}— suba la hoja {direccion === "egreso" ? "Egresos (601/701)" : "Ingresos (401)"} · año {year}.
                      </p>
                    )}
                    {preflightResult.showReclasificar !== false && !preflightResult.wrongFlujoOnly && (
                    <Button
                      type="button"
                      variant="outline"
                      className="h-7 text-[10px] w-fit"
                      disabled={preflightBusy || !clientId}
                      onClick={async () => {
                        setPreflightBusy(true);
                        try {
                          const { data } = await api.post("/excel/re-sync-flujo", null, {
                            params: {
                              client_id: clientId,
                              year,
                              upload_intent: getConfrontaFlujo(direccion),
                            },
                          });
                          toast.success(
                            `Reclasificación aplicada (${data?.updated ?? 0} fila(s) actualizadas). Vuelva a validar.`,
                          );
                          await validateContabilidad();
                        } catch (e) {
                          toast.error(formatApiError(e.response?.data?.detail) || "No se pudo reclasificar");
                        } finally {
                          setPreflightBusy(false);
                        }
                      }}
                    >
                      Reclasificar filas en BD (sin volver a subir Excel)
                    </Button>
                    )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <AlertDialog open={confirmRunOpen} onOpenChange={setConfirmRunOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {preflightResult?.suggestedYear
                    ? "Período sin coincidir con la contabilidad"
                    : (preflightResult?.title || "Contabilidad posiblemente incorrecta")}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {preflightResult?.message}
                  {" "}
                  Puede ejecutar la confronta igualmente (columnas Excel podrían quedar en $0 o solo mostrar datos del SAT).
                  {preflightResult?.suggestedYear
                    ? ` Se detectaron cobros en el año ${preflightResult.suggestedYear}.`
                    : null}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                {preflightResult?.suggestedYear ? (
                  <AlertDialogAction
                    className="bg-[#0F2D5C] hover:bg-[#0F2D5C]/90"
                    onClick={() => {
                      setConfirmRunOpen(false);
                      setYear(preflightResult.suggestedYear);
                      toast.info(`Año cambiado a ${preflightResult.suggestedYear}. Pulse de nuevo Ejecutar confronta.`);
                    }}
                  >
                    Usar año {preflightResult.suggestedYear}
                  </AlertDialogAction>
                ) : null}
                <AlertDialogAction
                  onClick={async () => {
                    setConfirmRunOpen(false);
                    await executeConfronta();
                  }}
                >
                  Ejecutar de todos modos
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Búsqueda libre por folio o UUID */}
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2 text-sm" style={{fontFamily:"'Chivo'"}}>
              {CF.TITLE_BUSCAR_CFDI}
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={searchQuery}
                onChange={(e)=>setSearchQuery(e.target.value)}
                onKeyDown={(e)=> e.key === "Enter" && runSearch()}
                placeholder="Folio (ej. 5703), serie+folio (CP-2352) o UUID completo / parcial"
                className="rounded-sm h-9 flex-1 min-w-[260px] text-xs"
                data-testid="cfdi-search-input"
              />
              <Button onClick={runSearch} disabled={searching} className="h-9 rounded-sm bg-[#0F2D5C] hover:bg-[#0F2D5C]/90 text-xs" data-testid="cfdi-search-btn">
                {searching ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : <Search className="w-3.5 h-3.5 mr-1.5"/>}
                Buscar
              </Button>
              {searchResults !== null && (
                <Button variant="outline" onClick={clearSearch} className="h-9 rounded-sm text-xs" data-testid="cfdi-search-clear">
                  <X className="w-3.5 h-3.5 mr-1.5"/> Limpiar
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => { setDiagnoseUuid(searchQuery); setDiagnoseOpen(true); }}
                disabled={!clientId}
                className="h-9 rounded-sm text-xs border-violet-300 text-violet-700 hover:bg-violet-50"
                data-testid="cfdi-diagnose-btn"
                title="Ver trazabilidad completa del UUID: PUE/PPD, parcialidades, NCs, Excel (Contabilidad) vs sistema (SAT)"
              >
                <Search className="w-3.5 h-3.5 mr-1.5"/> Diagnóstico
              </Button>
            </div>
            {searchResults !== null && (
              <div className="mt-3 border border-slate-200 rounded-sm overflow-hidden">
                {searchResults.length === 0 ? (
                  <div className="p-4 text-xs text-slate-500 text-center">Sin resultados.</div>
                ) : (
                  <>
                    {searchResults.some(r => r._otro_cliente) && (
                      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-xs text-amber-900">
                        <b>Aviso:</b> Algunos resultados pertenecen a OTROS clientes en tu sistema. Aparecen igual para que puedas verlos, pero las observaciones de la confronta solo aplican al cliente activo.
                      </div>
                    )}
                    {searchResults.some(r => r._origen === "excel") && (
                      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-xs text-amber-900">
                        <b>Hay resultados marcados "{CF.STAT_SOLO_EXCEL}":</b> el UUID aparece en el Control de IVA en Excel (Contabilidad) cargado, pero el XML correspondiente NO está en el sistema (SAT). Clic en la fila para ver los datos exactos del Excel y la observación.
                      </div>
                    )}
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Tipo</th>
                        <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Serie/Folio</th>
                        <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">UUID</th>
                        <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Fecha</th>
                        <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Dirección</th>
                        <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Cliente / Receptor</th>
                        <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Total</th>
                        <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Estado</th>
                        <th className="text-center px-2 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 w-14">Visor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map((r, i) => {
                        const tipos = { I: "Ingreso", E: "Egreso", P: "Pago", N: "Nómina", T: "Traslado" };
                        const tipoColor = {
                          I: "bg-emerald-100 text-emerald-800",
                          E: "bg-rose-100 text-rose-800",
                          P: "bg-blue-100 text-blue-800",
                          N: "bg-purple-100 text-purple-800",
                          T: "bg-slate-100 text-slate-800",
                        };
                        return (
                          <tr key={i} className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${r._otro_cliente ? "bg-amber-50/40":""} ${r._origen==="excel"?"bg-amber-50/60":""}`}
                              onClick={()=>openCfdiDetail(r.uuid, null, r.client_id, r._origen==="excel"?r:null)}
                              data-testid={`search-row-${r.uuid.slice(0,8)}`}>
                            <td className="px-3 py-1.5">
                              {r._origen === "excel" ? (
                                <span className="px-2 py-0.5 rounded-sm text-[10px] font-semibold bg-amber-100 text-amber-800">{CF.BADGE_SOLO_EXCEL_CORTO}</span>
                              ) : (
                                <span className={`px-2 py-0.5 rounded-sm text-[10px] font-semibold ${tipoColor[r.tipo_comprobante] || "bg-slate-100"}`}>{tipos[r.tipo_comprobante] || r.tipo_comprobante}</span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 font-mono">{[r.serie, r.folio].filter(Boolean).join("-") || "—"}</td>
                            <td className="px-3 py-1.5 font-mono text-[10px]">{r.uuid.slice(0,8)}…{r.uuid.slice(-4)}</td>
                            <td className="px-3 py-1.5 font-mono text-[10px]">{(r.fecha_pago || r.fecha || "").slice(0,10)}</td>
                            <td className="px-3 py-1.5 text-[10px] text-slate-500">{r.direccion === "emitido" ? "Emitido" : "Recibido"}</td>
                            <td className="px-3 py-1.5 text-slate-700">
                              {r.direccion === "emitido" ? r.nombre_receptor : r.nombre_emisor}
                              {r._otro_cliente && (
                                <div className="text-[10px] text-amber-700 mt-0.5">
                                  ⚠ Cliente: {r._cliente_nombre} ({r._cliente_rfc})
                                </div>
                              )}
                              {r._origen === "excel" && r._excel_data?.etiqueta && (
                                <div className="text-[10px] text-amber-700 mt-0.5">
                                  📋 Excel (Contabilidad) · {r._excel_data.etiqueta.slice(0,50)}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-right num">{fmtMoney(r.total)}</td>
                            <td className="px-3 py-1.5 text-[10px]">
                              <span className={r.estado === "Cancelado" ? "text-rose-600" : r.estado === CF.ESTADO_SOLO_EXCEL_TABLA ? "text-amber-700" : "text-emerald-600"}>{r.estado || "Vigente"}</span>
                              <Eye className="w-3 h-3 ml-2 inline text-slate-400"/>
                            </td>
                            <td className="px-2 py-1.5 text-center" onClick={(e) => e.stopPropagation()}>
                              {r._origen !== "excel" && r.uuid ? (
                                <button
                                  type="button"
                                  className="p-1 rounded-sm text-[#0F2D5C] hover:bg-slate-100 inline-flex"
                                  title="Ver CFDI y PDF aquí"
                                  onClick={() =>
                                    openInSatVisor(
                                      r.uuid,
                                      r.client_id && r.client_id !== clientId ? r.client_id : undefined
                                    )
                                  }
                                >
                                  <FileSearch className="w-3.5 h-3.5"/>
                                </button>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Resultado */}
          <div ref={confrontaResultsRef} data-testid="confronta-results-anchor" className="scroll-mt-4" />
          {confronta?.mode === "single" && (
            <ConfrontaDetail
              data={confronta.data}
              onOpenCfdi={openCfdiDetail}
              onOpenSatVisor={openInSatVisor}
              activeClientId={clientId}
            />
          )}

          {confronta?.mode === "multi" && (
            <MultiMonthView
              monthly={confronta.monthly}
              openMonths={openMonths}
              setOpenMonths={setOpenMonths}
              onOpenCfdi={openCfdiDetail}
              onOpenSatVisor={openInSatVisor}
              activeClientId={clientId}
              onDownloadDiferenciasMes={dlDiferenciasMes}
              onDownloadMismoMesFechas={dlMismoMesFechas}
            />
          )}
        </>
        )}
      </div>

      <CfdiConfrontaDetailDialog
        detail={cfdiDetail}
        onClose={() => setCfdiDetail(null)}
        onAfterMutate={runConfronta}
      />

      <DiagnoseUuidDialog
        open={diagnoseOpen}
        onClose={() => setDiagnoseOpen(false)}
        clientId={clientId}
        initialUuid={diagnoseUuid}
      />

      <ConfrontaDiferenciasBundleDialog
        open={bundleDialogOpen}
        onOpenChange={setBundleDialogOpen}
        clientId={clientId}
        client={selectedClient}
        fechaDesde={fechaDesde}
        fechaHasta={fechaHasta}
        direccion={direccion}
      />
    </>
  );
}

// ---- Helpers de meses ----
function buildMonthList(fechaDesde, fechaHasta) {
  // Genera lista de meses (con sus rangos from/to recortados al rango original)
  const [yA, mA, dA] = splitDate(fechaDesde);
  const [yB, mB, dB] = splitDate(fechaHasta);
  const out = [];
  let y = yA, m = mA;
  while (y < yB || (y === yB && m <= mB)) {
    const monthFirst = new Date(y, m - 1, 1);
    const monthLast = new Date(y, m, 0);
    const fromDate = (y === yA && m === mA) ? new Date(yA, mA - 1, dA) : monthFirst;
    const toDate = (y === yB && m === mB) ? new Date(yB, mB - 1, dB) : monthLast;
    const key = `${y}-${String(m).padStart(2,'0')}`;
    out.push({
      key,
      label: `${MES_FULL[m-1]} ${y}`,
      from: fromDate.toISOString().slice(0,10),
      to: toDate.toISOString().slice(0,10),
    });
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
  return out;
}

function sistemaMontosDesglose(totales, excelDisplayTotales = null) {
  return sistemaDesgloseParaResumen(totales, excelDisplayTotales);
}

const CONFRONTA_IVA_DETAIL_COLS = [
  { k: "uuid", label: "UUID", mono: true, render: (r) => r.uuid?.slice(0, 8) + "…" },
  { k: "folio", label: "Folio", render: (r) => r.folio },
  { k: "fecha", label: CF.FEC_SISTEMA_SAT, render: (r) => r.fecha_sistema || r.fecha },
  { k: "sis_pue", label: CF.TH_CFDI_SAT_PUE, right: true,
    render: (r) => <span className="text-emerald-700">{fmtMoney(r.subtotal_pue || r.subtotal || 0)}</span> },
  { k: "sis_pago", label: CF.TH_CFDI_SAT_PAGO, right: true,
    render: (r) => <span className="text-sky-700">{fmtMoney(r.subtotal_pago || 0)}</span> },
  { k: "iva_sis", label: CF.TH_IVA_SAT, right: true,
    render: (r) => <span className="font-medium">{fmtMoney(r.iva_sistema ?? r.iva ?? 0)}</span> },
  { k: "iva_exc", label: CF.TH_IVA_EXCEL, right: true, render: (r) => fmtMoney(r.iva_excel ?? 0) },
  { k: "diff_iva", label: "Δ IVA", right: true,
    render: (r) => {
      const d = r.diff_iva ?? ((r.iva_sistema ?? r.iva ?? 0) - (r.iva_excel ?? 0));
      return <span className={Math.abs(d) > 0.01 ? "text-amber-700 font-semibold" : ""}>{fmtMoney(d)}</span>;
    } },
  { k: "sis_tot", label: CF.TOTAL_SISTEMA_SAT, right: true,
    render: (r) => <span className="font-semibold">{fmtMoney(r.total_sistema ?? r.total ?? 0)}</span> },
];

// ---- Vista multi-mes ----
function MultiMonthView({
  monthly,
  openMonths,
  setOpenMonths,
  onOpenCfdi,
  onOpenSatVisor,
  activeClientId,
  onDownloadDiferenciasMes,
  onDownloadMismoMesFechas,
}) {
  const allMonthsBalanced =
    monthly.length > 0 && monthly.every((blk) => !confrontaHasDifferences(blk.data));

  // Agregados globales — incluyen el desglose PUE / Pago del sistema.
  const agg = monthly.reduce((acc, blk) => {
    const t = blk.data.totales;
    const ex = pickExcelDisplayTotales(t, { excelMeta: blk.data?.excel_meta });
    const sis = sistemaMontosDesglose(t, ex);
    acc.sistema.subtotal += sis.subtotal;
    acc.sistema.iva += sis.iva;
    acc.sistema.total += sis.total;
    acc.sistema.count += t.sistema.count;
    acc.sistema_pue.subtotal += sis.pueSub;
    acc.sistema_pue.iva += sis.pueIva;
    acc.sistema_pue.total += (t.sistema?.pue?.total || 0);
    acc.sistema_pue.count += (t.sistema?.pue?.count || 0);
    acc.sistema_pago.subtotal += sis.pagoSub;
    acc.sistema_pago.iva += sis.pagoIva;
    acc.sistema_pago.total += sis.pagoIva > 0.01 ? (t.sistema?.pago?.total || 0) : 0;
    acc.sistema_pago.count += sis.pagoIva > 0.01 ? (t.sistema?.pago?.count || 0) : 0;
    acc.excel.subtotal += ex.subtotal || 0;
    acc.excel.iva += ex.iva || 0;
    acc.excel.total += ex.total || 0;
    acc.excel.count += ex.count || 0;
    acc.diff.subtotal += t.diff.subtotal;
    acc.diff.iva += diffIvaContabilidadVsSistema(t, ex);
    acc.diff.total += t.diff.total;
    acc.summary.matched += blk.data.summary.matched;
    acc.summary.mismatched += blk.data.summary.mismatched;
    acc.summary.solo_en_excel += blk.data.summary.solo_en_excel;
    acc.summary.solo_en_sistema += blk.data.summary.solo_en_sistema;
    acc.summary.fuera_de_periodo += blk.data.summary.fuera_de_periodo;
    return acc;
  }, {
    sistema: { subtotal:0, iva:0, total:0, count:0 },
    sistema_pue:  { subtotal:0, iva:0, total:0, count:0 },
    sistema_pago: { subtotal:0, iva:0, total:0, count:0 },
    excel:   { subtotal:0, iva:0, total:0, count:0 },
    diff:    { subtotal:0, iva:0, total:0 },
    summary: { matched:0, mismatched:0, solo_en_excel:0, solo_en_sistema:0, fuera_de_periodo:0 },
  });

  const toggleMonth = (key) => setOpenMonths(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="space-y-4">
      {/* KPIs globales */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatBox label="Coincidentes" value={fmtNumber(agg.summary.matched)} color="emerald" icon={CheckCircle2}/>
        <StatBox label="Con diferencias" value={fmtNumber(agg.summary.mismatched)} color="amber" icon={AlertTriangle}/>
        <StatBox label={CF.STAT_SOLO_EXCEL} value={fmtNumber(agg.summary.solo_en_excel)} color="red" icon={ArrowRightLeft}/>
        <StatBox label={CF.STAT_SOLO_SISTEMA} value={fmtNumber(agg.summary.solo_en_sistema)} color="blue" icon={ArrowRightLeft}/>
        <StatBox label="Fuera de período" value={fmtNumber(agg.summary.fuera_de_periodo)} color="rose" icon={CalendarRange}/>
      </div>

      {monthly[0]?.data && <ExcelConfrontaDiagnostico data={monthly[0].data} />}

      {/* Panel agregado */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="grid grid-cols-3 text-xs">
          <ColumnTotals title={CF.CFDI_EN_SISTEMA_SAT} sub={agg.sistema.subtotal} iva={agg.sistema.iva} tot={agg.sistema.total} count={agg.sistema.count}/>
          <ColumnTotals title={CF.EXCEL_CONTABILIDAD} sub={agg.excel.subtotal} iva={agg.excel.iva} tot={agg.excel.total} count={agg.excel.count}/>
          <ColumnTotals title="Diferencia" sub={agg.diff.subtotal} iva={agg.diff.iva} tot={agg.diff.total} highlight/>
        </div>
      </div>

      {allMonthsBalanced && (
        <div
          className="rounded-sm border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-950"
          data-testid="confronta-sin-diferencias-periodo"
        >
          <b>Sin diferencias en el período.</b> El sistema (SAT) y la contabilidad (Excel, CSV o conciliación
          manual) coinciden en todos los meses confrontados. Si esperaba hallazgos, revise que las cargas usen la
          fecha de cobro/pago del mes y el cliente correcto.
        </div>
      )}

      {/* Tabla mensual con expandible */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
          <div className="text-sm font-semibold text-[#0F2D5C]" style={{fontFamily:"'Chivo'"}}>
            Resumen por mes — clic en una fila para ver el detalle
          </div>
          <p className="text-[11px] text-slate-600 mt-1">
            Los meses sin diferencias muestran un aviso al expandir la fila.{" "}
            <strong>IVA (SAT)</strong> = IVA PUE + IVA de complementos de pago vigentes (<em>fecha_pago</em> del mes; los cancelados no suman).
            Contalink suele alinear con <strong>IVA PUE</strong>; si hay Δ IVA, revise la pestaña «Sólo Visor SAT»
            (filas PPD) o «IVA».{" "}
            <strong>Dif IVA</strong> = IVA SAT (neto) − IVA Excel (contabilidad).{" "}
            <strong>Δ Total</strong> = SAT − Excel sólo en filas cruzadas por UUID (misma base que Coincidentes / Diferencias de monto).
          </p>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-xs min-w-[1520px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Mes</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">{CF.TH_CFDI_SIS_CORTO}</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-emerald-700">{CF.TH_CFDI_SAT_PUE}</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-sky-700">{CF.TH_CFDI_SAT_PAGO}</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-emerald-700" title="IVA de CFDIs PUE cobrados en el mes (emisión = cobro)">{CF.TH_IVA_SAT_PUE}</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-sky-700" title="IVA de complementos de pago (tipo P) con fecha_pago en el mes">{CF.TH_IVA_SAT_PAGO}</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-violet-700" title="IVA PUE + IVA Pago">{CF.TH_IVA_SAT}</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-[#0F2D5C]">{CF.TH_TOTAL_SIS}</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">{CF.TH_SUB_EXCEL}</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">{CF.TH_IVA_EXCEL}</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">{CF.TH_TOTAL_EXCEL}</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Dif IVA</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Δ Total</th>
              <th className="w-10 border-l border-slate-200 bg-slate-50 p-0" aria-hidden />
            </tr>
          </thead>
          <tbody>
            {monthly.map((blk) => {
              const isOpen = !!openMonths[blk.key];
              const t = blk.data.totales;
              const ex = pickExcelDisplayTotales(t, { excelMeta: blk.data?.excel_meta });
              const sis = sistemaMontosDesglose(t, ex);
              const diffIvaCont = diffIvaContabilidadVsSistema(t, ex);
              const hasDiff = confrontaHasDifferences(blk.data);
              const noData = blk.data.summary.matched === 0 && blk.data.summary.mismatched === 0 &&
                             blk.data.summary.solo_en_excel === 0 && blk.data.summary.solo_en_sistema === 0;
              return (
                <Fragment key={blk.key}>
                  <tr
                    onClick={() => toggleMonth(blk.key)}
                    className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${hasDiff ? "bg-amber-50/30" : ""} ${noData ? "opacity-60" : ""}`}
                    data-testid={`month-row-${blk.key}`}
                  >
                    <td className="px-3 py-2 font-medium text-slate-800">{blk.label}</td>
                    <td className="px-3 py-2 text-right num">{fmtNumber(t.sistema.count)}</td>
                    <td className="px-3 py-2 text-right num text-emerald-700">{fmtMoney(sis.pueSub)}</td>
                    <td className="px-3 py-2 text-right num text-sky-700">{fmtMoney(sis.pagoSub)}</td>
                    <td className="px-3 py-2 text-right num text-emerald-800" data-testid={`iva-pue-${blk.key}`}>{fmtMoney(sis.pueIva)}</td>
                    <td className="px-3 py-2 text-right num text-sky-800" data-testid={`iva-pago-${blk.key}`}>{fmtMoney(sis.pagoIva)}</td>
                    <td className="px-3 py-2 text-right num text-violet-800" data-testid={`iva-sat-${blk.key}`}>{fmtMoney(sis.iva)}</td>
                    <td className="px-3 py-2 text-right num font-semibold text-[#0F2D5C]">{fmtMoney(sis.total)}</td>
                    <td className="px-3 py-2 text-right num">{fmtMoney(ex.subtotal)}</td>
                    <td className="px-3 py-2 text-right num">{fmtMoney(ex.iva)}</td>
                    <td className="px-3 py-2 text-right num">{fmtMoney(ex.total)}</td>
                    <td
                      className={`px-3 py-2 text-right num ${Math.abs(diffIvaCont) > 0.01 ? "text-amber-700 font-semibold" : ""}`}
                      data-testid={`diff-iva-${blk.key}`}
                      title={
                        confrontaComplementoPIvaPendiente(t) > 0.01
                          ? "Complemento de pago en SAT pendiente de cancelar o ajustar"
                          : undefined
                      }
                    >
                      {fmtMoney(diffIvaCont)}
                    </td>
                    <td className={`px-3 py-2 text-right num ${Math.abs(t.diff.total) > 0.01 ? "text-amber-700 font-semibold" : ""}`}>{fmtMoney(t.diff.total)}</td>
                    <SatExpandTableRailCell open={isOpen} />
                  </tr>
                  {isOpen && (
                    <tr className="bg-slate-50/50">
                      <td colSpan={14} className="p-4">
                        <div className="bg-white border border-slate-200 rounded-sm p-3">
                          <div className="flex items-center justify-end mb-3">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-7 rounded-sm text-xs gap-1.5 border-slate-300 text-slate-700"
                              onClick={() => toggleMonth(blk.key)}
                              data-testid={`month-detail-fold-${blk.key}`}
                            >
                              <ChevronUp className="w-3.5 h-3.5" aria-hidden />
                              Plegar mes
                            </Button>
                          </div>
                          {hasDiff ? (
                            <div className="space-y-4">
                              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                                Detalle de {blk.label} · {blk.from} → {blk.to}
                              </div>
                              <ConfrontaDetail
                                key={blk.key}
                                data={blk.data}
                                embedded
                                onOpenCfdi={onOpenCfdi}
                                onOpenSatVisor={onOpenSatVisor}
                                activeClientId={activeClientId}
                              />
                            </div>
                          ) : (
                            <div className="space-y-4">
                              <MesSinDiferenciasAviso
                                label={blk.label}
                                from={blk.from}
                                to={blk.to}
                                data={blk.data}
                                onDownloadMismoMes={
                                  onDownloadMismoMesFechas
                                    ? () => onDownloadMismoMesFechas(blk.from, blk.to)
                                    : undefined
                                }
                              />
                              <ConfrontaDetail
                                key={blk.key}
                                data={blk.data}
                                embedded
                                onOpenCfdi={onOpenCfdi}
                                onOpenSatVisor={onOpenSatVisor}
                                activeClientId={activeClientId}
                              />
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot className="bg-slate-100 border-t-2 border-slate-300">
            <tr>
              <td className="px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-700">TOTAL</td>
              <td className="px-3 py-2 text-right num font-bold">{fmtNumber(agg.sistema.count)}</td>
              <td className="px-3 py-2 text-right num font-bold text-emerald-700">{fmtMoney(agg.sistema_pue.subtotal)}</td>
              <td className="px-3 py-2 text-right num font-bold text-sky-700">{fmtMoney(agg.sistema_pago.subtotal)}</td>
              <td className="px-3 py-2 text-right num font-bold text-violet-800">{fmtMoney(agg.sistema.iva)}</td>
              <td className="px-3 py-2 text-right num font-bold text-[#0F2D5C]">{fmtMoney(agg.sistema.total)}</td>
              <td className="px-3 py-2 text-right num font-bold">{fmtMoney(agg.excel.subtotal)}</td>
              <td className="px-3 py-2 text-right num font-bold">{fmtMoney(agg.excel.iva)}</td>
              <td className="px-3 py-2 text-right num font-bold">{fmtMoney(agg.excel.total)}</td>
              <td className="px-3 py-2 text-right num font-bold text-amber-700">{fmtMoney(agg.diff.iva)}</td>
              <td className="px-3 py-2 text-right num font-bold text-amber-700">{fmtMoney(agg.diff.total)}</td>
              <td className="w-10 border-l border-slate-300 bg-slate-100 p-0" aria-hidden />
            </tr>
          </tfoot>
        </table>
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, color, icon: Icon }) {
  const colors = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    amber:   "border-amber-200 bg-amber-50 text-amber-800",
    blue:    "border-blue-200 bg-blue-50 text-blue-800",
    red:     "border-red-200 bg-red-50 text-red-800",
    slate:   "border-slate-200 bg-slate-50 text-slate-800",
    rose:    "border-rose-200 bg-rose-50 text-rose-800",
  };
  return (
    <div className={`border rounded-sm p-3 ${colors[color]}`} data-testid={`stat-${label.toLowerCase().replace(/ /g,'-')}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold opacity-80">
        <Icon className="w-3 h-3"/> {label}
      </div>
      <div className="num text-xl font-bold mt-1">{value}</div>
    </div>
  );
}

function ConfrontaDetail({ data: rawData, embedded, onOpenCfdi, onOpenSatVisor, activeClientId }) {
  const data = normalizeConfrontaResponse(rawData, "cobrados");
  const t = data.totales;
  const ex = pickExcelDisplayTotales(t, { excelMeta: data?.excel_meta });
  const s = data.summary;
  const consideracionesCount = Number(data?.consideraciones_sat?.count) || 0;
  const sis = sistemaMontosDesglose(t, ex);
  const ivaTabRows = [
    ...(data.mismatched || []),
    ...(data.matched || []).filter((r) => Math.abs(r.diff_iva || 0) > 0.01),
    ...(data.solo_en_sistema || []),
    ...(data.solo_en_excel || []),
    ...(data.fuera_de_periodo || []),
  ];
  const ivaTabCount = ivaTabRows.length;
  const fxTabCount = Number(s.diferencial_cambiario) || (data.filas_diferencial_cambiario || []).length;
  const tabTriggerClass =
    "rounded-sm flex-1 min-w-0 text-[10px] sm:text-xs px-1.5 sm:px-2 py-2 h-auto min-h-9 whitespace-normal text-center leading-tight";
  return (
    <div className="space-y-4" data-testid="confronta-detail">
      <RetencionesInformativasAviso rows={data.matched} />
      {!embedded && (
        <>
          <ConfrontaEngineBadge data={data} />
          <ExcelConfrontaDiagnostico data={data} />
          <ConfrontaHuecoCruceBanner data={data} />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatBox label="Coincidentes" value={fmtNumber(s.matched)} color="emerald" icon={CheckCircle2}/>
            <StatBox label="Con diferencias" value={fmtNumber(s.mismatched)} color="amber" icon={AlertTriangle}/>
            <StatBox label={CF.STAT_SOLO_EXCEL} value={fmtNumber(s.solo_en_excel)} color="red" icon={ArrowRightLeft}/>
            <StatBox label={CF.STAT_SOLO_SISTEMA} value={fmtNumber(s.solo_en_sistema)} color="blue" icon={ArrowRightLeft}/>
            <StatBox label="Fuera de período" value={fmtNumber(s.fuera_de_periodo)} color="rose" icon={CalendarRange}/>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="grid grid-cols-3 text-xs">
              <ColumnTotals title={CF.CFDI_EN_SISTEMA_SAT} sub={sis.subtotal} iva={sis.iva} tot={sis.total} count={t.sistema.count}/>
              <ColumnTotals title={CF.EXCEL_CONTABILIDAD} sub={ex.subtotal} iva={ex.iva} tot={ex.total} count={ex.count}/>
              <ColumnTotals title="Diferencia (cruce UUID)" sub={t.diff.subtotal} iva={t.diff.iva} tot={t.diff.total} highlight/>
            </div>
          </div>
        </>
      )}

      <Tabs defaultValue="diferencias" className={embedded ? "w-full" : "bg-white border border-slate-200 rounded-sm p-4 w-full"}>
        <TabsList className="rounded-sm bg-slate-100 w-full grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-9 gap-1 p-1 h-auto">
          <TabsTrigger value="diferencias" className={tabTriggerClass} data-testid="tab-diferencias">Reporte de diferencias</TabsTrigger>
          <TabsTrigger value="mismatched" className={tabTriggerClass} data-testid="tab-mismatched">Diferencias de monto ({s.mismatched})</TabsTrigger>
          <TabsTrigger value="fx" className={tabTriggerClass} data-testid="tab-fx-diferencial">
            Diferencial cambiario ({fxTabCount})
          </TabsTrigger>
          <TabsTrigger value="fuera" className={tabTriggerClass} data-testid="tab-fuera">Fuera de período ({s.fuera_de_periodo})</TabsTrigger>
          <TabsTrigger value="only_excel" className={tabTriggerClass} data-testid="tab-only-excel">{CF.TAB_SOLO_EXCEL} ({s.solo_en_excel})</TabsTrigger>
          <TabsTrigger value="only_sis" className={tabTriggerClass} data-testid="tab-only-sis">{CF.TAB_SOLO_SISTEMA} ({s.solo_en_sistema})</TabsTrigger>
          <TabsTrigger value="matched" className={tabTriggerClass} data-testid="tab-matched">Coincidentes ({s.matched})</TabsTrigger>
          <TabsTrigger value="iva" className={tabTriggerClass} data-testid="tab-iva">
            {CF.TAB_IVA} ({ivaTabCount})
          </TabsTrigger>
          <TabsTrigger value="consideraciones" className={tabTriggerClass} data-testid="tab-consideraciones">
            {CF.TAB_CONSIDERACIONES} ({consideracionesCount})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="diferencias">
          <DiferenciasReport
            data={data}
            onOpenCfdi={onOpenCfdi}
            onOpenSatVisor={onOpenSatVisor}
            activeClientId={activeClientId}
          />
        </TabsContent>

        <TabsContent value="mismatched">
          <DetailTable rows={data.mismatched} onOpenCfdi={onOpenCfdi} onOpenSatVisor={onOpenSatVisor} activeClientId={activeClientId} obsBuilder={(r)=>buildObsDiferencia(r)}
            cols={[
              {k:"uuid", label:"UUID", mono:true, render:(r)=>r.uuid?.slice(0,8)+"…"},
              {k:"mon", label:"Mon.", render:(r)=><MonedaConfrontaBadge row={r} />},
              {k:"folio", label:"Folio", render:(r)=>r.folio},
              {k:"fecha_sis", label: CF.FEC_SISTEMA_SAT, render:(r)=>r.fecha_sistema},
              {k:"fecha_exc", label: CF.FEC_EXCEL_CONTAB, render:(r)=>r.fecha_excel},
              {k:"cliente", label:"Cliente", render:(r)=>r.cliente},
              {k:"sis_pue", label: CF.TH_CFDI_SAT_PUE, right:true,
                render:(r)=><span className="text-emerald-700">{fmtMoney(r.subtotal_pue || 0)}</span>},
              {k:"sis_pago", label: CF.TH_CFDI_SAT_PAGO, right:true,
                render:(r)=><span className="text-sky-700">{fmtMoney(r.subtotal_pago || 0)}</span>},
              {k:"iva_sis", label: CF.TH_IVA_SAT, right:true,
                render:(r)=><span className="font-medium">{fmtMoney(r.iva_sistema || 0)}</span>},
              {k:"iva_exc", label: CF.TH_IVA_EXCEL, right:true, render:(r)=>fmtMoney(r.iva_excel || 0)},
              {k:"diff_iva", label:"Δ IVA", right:true,
                render:(r)=><span className="text-amber-700 font-semibold">{fmtMoney(r.diff_iva || 0)}</span>},
              {k:"sis_tot", label: CF.TOTAL_SISTEMA_SAT, right:true,
                render:(r)=><span className="font-semibold">{fmtMoney(r.total_sistema)}</span>},
              {k:"tot_e", label: CF.TOTAL_EXCEL_CONTAB, right:true, render:(r)=>fmtMoney(r.total_excel)},
              {k:"diff_t", label:"Δ Total", right:true,
                render:(r)=><span className="text-amber-700 font-semibold">{fmtMoney(r.diff_total)}</span>},
            ]}/>
        </TabsContent>

        <TabsContent value="fx">
          <p className="text-xs text-slate-500 px-2 py-2">
            Lista informativa: filas en moneda extranjera con diferencia de monto que puede deberse a tipo de cambio distinto
            (T.C. factura SAT vs contabilidad/banco). No bloquea la operación ni reclasifica el error principal.
          </p>
          <DetailTable
            rows={data.filas_diferencial_cambiario || []}
            onOpenCfdi={onOpenCfdi}
            onOpenSatVisor={onOpenSatVisor}
            activeClientId={activeClientId}
            obsBuilder={(r) => ({
              titulo: `Diferencial cambiario · ${r.moneda_origen || "—"}`,
              detalle: r.nota || "Diferencia probable por tipo de cambio.",
              color: "sky",
            })}
            cols={[
              { k: "uuid", label: "UUID", mono: true, render: (r) => `${r.uuid?.slice(0, 8)}…` },
              { k: "mon", label: "Mon.", render: (r) => r.moneda_origen || "—" },
              { k: "tc_xml", label: "T.C. XML", right: true, render: (r) => r.tipo_cambio_xml ?? "—" },
              { k: "tc_cont", label: "T.C. contab.", right: true, render: (r) => r.tipo_cambio_contable_implicito ?? "—" },
              { k: "delta_tc", label: "Δ T.C.", right: true, render: (r) => (r.delta_tc != null ? r.delta_tc : "—") },
              { k: "sis", label: CF.TOTAL_SISTEMA_SAT, right: true, render: (r) => fmtMoney(r.total_sistema) },
              { k: "exc", label: CF.TOTAL_EXCEL_CONTAB, right: true, render: (r) => fmtMoney(r.total_excel) },
              { k: "diff", label: "Δ Total", right: true, render: (r) => <span className="text-sky-700 font-semibold">{fmtMoney(r.diff_total)}</span> },
              { k: "estado", label: "Estado", render: (r) => r.estado },
            ]}
          />
        </TabsContent>

        <TabsContent value="fuera">
          <p className="text-xs text-slate-500 px-2 py-2">UUIDs que en el Excel (Contabilidad) están en este período pero el sistema (SAT) los registró con otra fecha de cobro.</p>
          <DetailTable rows={data.fuera_de_periodo} onOpenCfdi={onOpenCfdi} onOpenSatVisor={onOpenSatVisor} activeClientId={activeClientId} obsBuilder={(r)=>buildObsFuera(r)}
            cols={[
              {k:"uuid", label:"UUID", mono:true, render:(r)=>r.uuid?.slice(0,8)+"…"},
              {k:"folio", label:"Folio", render:(r)=>r.folio},
              {k:"fecha_exc", label: CF.FEC_EXCEL_CONTAB, render:(r)=>r.fecha_excel},
              {k:"fecha_sis", label: CF.FEC_SISTEMA_REAL_SAT, render:(r)=><span className="text-rose-700 font-semibold">{r.fecha_sistema}</span>},
              {k:"cliente", label:"Cliente", render:(r)=>r.cliente},
              {k:"mp", label:"Método", render:(r)=>r.metodo_pago},
              {k:"nota", label:"Nota", render:(r)=> r.emparejado_por_factura_relacionada
                ? <span className="font-semibold text-sky-800">Emparejado por factura relacionada</span>
                : "—"},
              {k:"sub_e", label: "Sub " + CF.EXCEL_CONTABILIDAD, right:true, render:(r)=>fmtMoney(r.subtotal_excel)},
              {k:"iva_e", label: "IVA " + CF.EXCEL_CONTABILIDAD, right:true, render:(r)=>fmtMoney(r.iva_excel)},
            ]}/>
        </TabsContent>
        <TabsContent value="only_excel">
          <p className="text-xs text-slate-500 px-2 py-2">Filas del Excel (Contabilidad) que NO existen en el sistema (SAT) (CFDI no cargado o no cobrado).</p>
          <DetailTable rows={data.solo_en_excel} onOpenCfdi={onOpenCfdi} onOpenSatVisor={onOpenSatVisor} activeClientId={activeClientId} obsBuilder={(r)=>buildObsSoloExcel(r)}
            cols={[
              {k:"uuid", label:"UUID", mono:true, render:(r)=>r.uuid ? r.uuid.slice(0,8)+"…" : <span className="text-slate-400">(sin UUID)</span>},
              {k:"folio", label:"Folio", render:(r)=>r.folio},
              {k:"fec", label:"Fec ingreso", render:(r)=>r.fecha_excel},
              {k:"cliente", label:"Cliente", render:(r)=>r.cliente},
              {k:"rfc", label:"RFC", mono:true, render:(r)=>r.rfc},
              {k:"mp", label:"Método", render:(r)=>r.metodo_pago},
              {k:"sub", label:"Subtotal", right:true, render:(r)=>fmtMoney(r.subtotal_excel)},
              {k:"iva", label:"IVA", right:true, render:(r)=>fmtMoney(r.iva_excel)},
              {k:"tot", label:"Total", right:true, render:(r)=>fmtMoney(r.total_excel)},
              {k:"obs", label:"Estatus", render:(r)=> r.justificacion === "Cancelado_local" || r.justificacion === "Cancelado en SAT" ? <span className="text-rose-600 font-bold" title={r.cancel_source || ""}>CANCELADO (local)</span> : <span className="text-slate-400">{CF.NO_EN_SISTEMA_SAT}</span>},
            ]}/>
        </TabsContent>
        <TabsContent value="only_sis">
          <p className="text-xs text-slate-500 px-2 py-2">CFDI cobrados en el sistema (SAT) (PUE o Complemento) que no aparecen en el Excel (Contabilidad).</p>
          <DetailTable rows={data.solo_en_sistema} onOpenCfdi={onOpenCfdi} onOpenSatVisor={onOpenSatVisor} activeClientId={activeClientId} obsBuilder={(r)=>buildObsSoloSistema(r)}
            cols={[
              {k:"uuid", label:"UUID", mono:true, render:(r)=>r.uuid?.slice(0,8)+"…"},
              {k:"folio", label:"Folio", render:(r)=>r.folio},
              {k:"fecha", label: CF.FEC_SISTEMA_SAT, render:(r)=>r.fecha_sistema},
              {k:"fuente", label:"Fuente", render:(r)=>r.fuente},
              {k:"cliente", label:"Cliente", render:(r)=>r.cliente},
              {k:"rfc", label:"RFC", mono:true, render:(r)=>r.rfc},
              {k:"sis_pue", label: CF.TH_CFDI_SAT_PUE, right:true,
                render:(r)=><span className="text-emerald-700">{fmtMoney(r.subtotal_pue || r.subtotal || 0)}</span>},
              {k:"sis_pago", label: CF.TH_CFDI_SAT_PAGO, right:true,
                render:(r)=><span className="text-sky-700">{fmtMoney(r.subtotal_pago || 0)}</span>},
              {k:"iva_sis", label: CF.TH_IVA_SAT, right:true, render:(r)=>fmtMoney(r.iva_sistema ?? r.iva ?? 0)},
              {k:"sis_tot", label: CF.TOTAL_SISTEMA_SAT, right:true,
                render:(r)=><span className="font-semibold">{fmtMoney(r.total)}</span>},
            ]}/>
        </TabsContent>
        <TabsContent value="matched">
          <DetailTable rows={data.matched} onOpenCfdi={onOpenCfdi} onOpenSatVisor={onOpenSatVisor} activeClientId={activeClientId} obsBuilder={(r)=>buildObsCoincide(r)}
            cols={[
              {k:"uuid", label:"UUID", mono:true, render:(r)=>r.uuid?.slice(0,8)+"…"},
              {k:"folio", label:"Folio", render:(r)=>r.folio},
              {k:"fecha", label:"Fecha", render:(r)=>r.fecha_sistema},
              {k:"cliente", label:"Cliente", render:(r)=>r.cliente},
              {k:"sis_pue", label: CF.TH_CFDI_SAT_PUE, right:true,
                render:(r)=><span className="text-emerald-700">{fmtMoney(r.subtotal_pue || 0)}</span>},
              {k:"sis_pago", label: CF.TH_CFDI_SAT_PAGO, right:true,
                render:(r)=><span className="text-sky-700">{fmtMoney(r.subtotal_pago || 0)}</span>},
              {k:"iva_sis", label: CF.TH_IVA_SAT, right:true, render:(r)=>fmtMoney(r.iva_sistema || 0)},
              {k:"iva_exc", label: CF.TH_IVA_EXCEL, right:true, render:(r)=>fmtMoney(r.iva_excel || 0)},
              {k:"sis_tot", label: CF.TOTAL_SISTEMA_SAT, right:true,
                render:(r)=><span className="font-semibold">{fmtMoney(r.total_sistema)}</span>},
              {k:"exc_tot", label: CF.TOTAL_EXCEL_CONTAB, right:true, render:(r)=>fmtMoney(r.total_excel)},
              {k:"rets", label:"Retenciones (info)", right:true, render:(r)=> r.retenciones_informativas
                ? <span className="text-sky-800" title={r.nota_retenciones || ""}>{fmtMoney((Number(r.retenciones_total)||0) + (Number(r.traslados_locales_sat)||0))}</span>
                : <span className="text-slate-300">—</span>},
            ]}/>
        </TabsContent>
        <TabsContent value="iva">
          <IvaConfrontaTab
            sis={sis}
            ex={ex}
            diffIva={diffIvaContabilidadVsSistema(t, ex)}
            diffIvaCruce={t.diff.iva}
            rows={ivaTabRows}
            onOpenCfdi={onOpenCfdi}
            onOpenSatVisor={onOpenSatVisor}
            activeClientId={activeClientId}
          />
        </TabsContent>
        <TabsContent value="consideraciones">
          <ConfrontaConsideracionesTab
            consideraciones={data.consideraciones_sat}
            onOpenSatVisor={onOpenSatVisor}
            activeClientId={activeClientId}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function IvaConfrontaTab({ sis, ex, diffIva, diffIvaCruce, rows, onOpenCfdi, onOpenSatVisor, activeClientId }) {
  return (
    <div className="space-y-4" data-testid="confronta-iva-tab">
      <p className="text-xs text-slate-600 px-1">
        Desglose de IVA del sistema (SAT): PUE y complementos de pago por <strong>subtotal</strong> (sin IVA) en las
        columnas verdes/azules; el IVA se muestra aparte para alinear con Sub / IVA / Total de Excel (Contabilidad).
      </p>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
        <div className="border border-emerald-200 bg-emerald-50 rounded-sm p-3">
          <div className="text-[10px] uppercase font-semibold text-emerald-800">IVA PUE (SAT)</div>
          <div className="num font-bold text-lg mt-1">{fmtMoney(sis.pueIva)}</div>
          <div className="text-[10px] text-emerald-900 mt-1">Sub PUE: {fmtMoney(sis.pueSub)}</div>
        </div>
        <div className="border border-sky-200 bg-sky-50 rounded-sm p-3">
          <div className="text-[10px] uppercase font-semibold text-sky-800">IVA Pago (SAT)</div>
          <div className="num font-bold text-lg mt-1">{fmtMoney(sis.pagoIva)}</div>
          <div className="text-[10px] text-sky-900 mt-1">Sub Pago: {fmtMoney(sis.pagoSub)}</div>
        </div>
        <div className="border border-violet-200 bg-violet-50 rounded-sm p-3">
          <div className="text-[10px] uppercase font-semibold text-violet-900">{CF.TH_IVA_SAT}</div>
          <div className="num font-bold text-lg mt-1">{fmtMoney(sis.iva)}</div>
        </div>
        <div className="border border-slate-200 bg-slate-50 rounded-sm p-3">
          <div className="text-[10px] uppercase font-semibold text-slate-700">{CF.TH_IVA_EXCEL}</div>
          <div className="num font-bold text-lg mt-1">{fmtMoney(ex.iva)}</div>
        </div>
        <div className="border border-amber-200 bg-amber-50 rounded-sm p-3">
          <div className="text-[10px] uppercase font-semibold text-amber-900">Δ IVA (SAT neto − Excel)</div>
          <div className={`num font-bold text-lg mt-1 ${Math.abs(diffIva) > 0.01 ? "text-amber-800" : ""}`}>
            {fmtMoney(diffIva)}
          </div>
          {diffIvaCruce != null && Math.abs((diffIvaCruce || 0) - (diffIva || 0)) > 0.01 ? (
            <div className="text-[10px] text-amber-900 mt-1">Δ IVA cruce UUID: {fmtMoney(diffIvaCruce)}</div>
          ) : null}
        </div>
      </div>
      <DetailTable
        rows={rows}
        onOpenCfdi={onOpenCfdi}
        onOpenSatVisor={onOpenSatVisor}
        activeClientId={activeClientId}
        obsBuilder={(r) => {
          if (r.diff_iva != null && r.total_sistema != null) return buildObsDiferencia(r);
          if (r.subtotal_excel != null && r.iva_excel != null && r.total_sistema == null) return buildObsSoloExcel(r);
          return buildObsSoloSistema(r);
        }}
        cols={CONFRONTA_IVA_DETAIL_COLS}
      />
    </div>
  );
}

function ColumnTotals({ title, sub, iva, tot, count, highlight }) {
  const cls = highlight ? "bg-amber-50 border-amber-200" : "bg-white";
  return (
    <div className={`p-4 border-r border-slate-200 last:border-r-0 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">{title}</div>
      <div className="space-y-1">
        <Row label="Subtotal" v={fmtMoney(sub)} />
        <Row label="IVA" v={fmtMoney(iva)} />
        <Row label="Total" v={fmtMoney(tot)} bold/>
        {count !== undefined && <Row label={CF.ROW_LABEL_CFDI_SAT} v={fmtNumber(count)} small/>}
      </div>
    </div>
  );
}
function Row({ label, v, bold, small }) {
  return (
    <div className="flex justify-between text-xs">
      <span className={`text-slate-500 ${small ? "text-[10px]" : ""}`}>{label}</span>
      <span className={`num ${bold ? "font-bold text-slate-900" : "text-slate-800"}`}>{v}</span>
    </div>
  );
}

// ---- Generadores de observación por tipo de diferencia ----
function MonedaConfrontaBadge({ row }) {
  const mon = String(row?.moneda_origen || "MXN").toUpperCase();
  const conv = row?.mxn_conversion || "mxn_native";
  if (conv === "excel_fallback") {
    return (
      <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-semibold bg-amber-100 text-amber-900 border border-amber-200" title="TC ausente en XML; se usó cobrado Excel (MXN)">
        TC→Excel
      </span>
    );
  }
  if (mon !== "MXN" && mon !== "XXX") {
    const tc = row?.tipo_cambio_xml;
    return (
      <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-semibold bg-sky-100 text-sky-900 border border-sky-200" title={tc ? `Tipo de cambio XML: ${tc}` : "Moneda extranjera convertida a MXN"}>
        {mon}{tc ? ` · ${tc}` : ""}
      </span>
    );
  }
  return null;
}

function buildObsDiferencia(r) {
  const dSub = r.diff_subtotal || 0;
  const dIva = r.diff_iva || 0;
  const dTot = r.diff_total || 0;
  const esPpd = r.fuente === "PPD" || String(r.metodo_pago || "").toUpperCase().includes("PPD");
  const tipo = esPpd ? "Complemento de Pago" : "CFDI";
  const mon = String(r.moneda_origen || "MXN").toUpperCase();
  const monedaNota = mon !== "MXN" && mon !== "XXX"
    ? `\n• Moneda CFDI: ${mon}${r.tipo_cambio_xml ? ` (T.C. XML ${r.tipo_cambio_xml})` : ""}. Confronta compara en MXN homogéneo.`
    : "";
  const fallbackNota = r.mxn_fallback_excel
    ? "\n• TC ausente en XML: el lado SAT usó el cobrado Excel (MXN) como referencia."
    : "";
  const fxNota = r.probable_diferencial_cambiario
    ? `\n\n• Posible diferencial cambiario (informativo): ${r.nota_diferencial_cambiario || "tipo de cambio distinto entre SAT y contabilidad."}`
    : "";
  const extraComps = esPpd ? textoVariosComplementos(r) : "";
  const notasClasificacion = [
    r.nota_coherencia_xml,
    r.nota_parcialidad,
    r.nota_base_exenta,
    r.nota_subtotal,
    r.diferencia_retenciones ? r.nota_retenciones : "",
    r.nota_centavos,
  ].filter(Boolean).map((t) => `\n• ${t}`).join("");

  if (r.ppd_parcial_vs_factura_excel) {
    const uuidPago = r.uuid_pago ? `\nComplemento de pago (SAT): ${r.uuid_pago}` : "";
    const factura = r.total_factura_excel != null ? fmtMoney(r.total_factura_excel) : "—";
    return {
      titulo: `Pago parcial en SAT vs factura total en Excel · ${tipo}`,
      detalle:
        `La factura PPD ${r.uuid} está en ambos lados, pero comparan criterios distintos:\n` +
        `• Sistema (SAT): cobró ${fmtMoney(r.total_sistema)} en este período (suma de complementos tipo P).${uuidPago}\n` +
        `• Excel (Contabilidad): registra ${fmtMoney(r.total_excel)} como cobro del período, ` +
        `pero el total de la factura en el archivo es ${factura}.\n` +
        `• Diferencia de cobro en el período: ${fmtMoney(dTot)}\n\n` +
        `Acción sugerida: en el Excel capture el importe cobrado del mes (columnas «Importe del ingreso» o «Cobrado tasa 16%»), ` +
        `o registre el complemento de pago con su UUID, no solo el total de la factura emitida.${monedaNota}${fallbackNota}${fxNota}${extraComps}`,
      color: "amber",
    };
  }

  if (esPpd && Math.abs(dTot) <= 0.02 && (r.total_excel || 0) < 0.01 && (r.total_sistema || 0) > 0) {
    return {
      titulo: `Cobro en SAT sin registro de pago en Excel · ${tipo}`,
      detalle:
        `El SAT registró un cobro de ${fmtMoney(r.total_sistema)} en este período para la factura PPD ${r.uuid}, ` +
        `pero el Excel no trae importe de cobro del mes (solo la factura o fila sin «Importe del ingreso»).\n` +
        `• Diferencia: ${fmtMoney(dTot)}\n\n` +
        `Acción sugerida: agregue en contabilidad el cobro del período (${fmtMoney(r.total_sistema)}) ` +
        `o el complemento de pago correspondiente.${extraComps}`,
      color: "amber",
    };
  }

  const tituloDup =
    esPpd && (r.posible_duplicado_complementos || (r.n_complementos_pago || 0) >= 2)
      ? `Diferencia de monto · posibles complementos duplicados · ${tipo}`
      : `Diferencia de monto detectada · ${tipo}`;

  return {
    titulo: tituloDup,
    detalle: `El CFDI con UUID ${r.uuid} está en ambos lados (Sistema (SAT) y Excel (Contabilidad)) pero los importes no coinciden:\n` +
             `• Subtotal sistema (SAT): ${fmtMoney(r.subtotal_sistema)} vs Excel (Contabilidad): ${fmtMoney(r.subtotal_excel)} → diferencia ${fmtMoney(dSub)}\n` +
             `• IVA sistema (SAT): ${fmtMoney(r.iva_sistema)} vs Excel (Contabilidad): ${fmtMoney(r.iva_excel)} → diferencia ${fmtMoney(dIva)}\n` +
             `• Total sistema (SAT): ${fmtMoney(r.total_sistema)} vs Excel (Contabilidad): ${fmtMoney(r.total_excel)} → diferencia ${fmtMoney(dTot)}${monedaNota}${fallbackNota}${fxNota}\n\n` +
             (esPpd
               ? `Acción sugerida: revise en el Excel el importe efectivamente cobrado en el mes y el UUID del complemento de pago; corrija Subtotal, IVA o Total para que coincidan con el cobro registrado por el SAT.`
               : `Acción sugerida: compare la fila del Excel con el XML de esta factura y corrija Subtotal, IVA o Total. Si el CFDI está en moneda extranjera, valide también el tipo de cambio aplicado.`) +
             extraComps + notasClasificacion,
    color: "amber",
  };
}

/** Texto adicional cuando hay 2+ complementos P vigentes sobre la misma factura. */
function textoVariosComplementos(r) {
  let vigentes = Array.isArray(r.complementos_pago_vigentes) ? r.complementos_pago_vigentes : null;
  if (!vigentes || vigentes.length < 2) {
    const byUuid = {};
    for (const raw of r.complementos_pago || []) {
      if (!raw || raw.cancelado) continue;
      const uuidPago = String(raw.uuid_pago || "").toUpperCase().trim();
      if (!uuidPago) continue;
      const imp = Number(raw.imp_pagado || 0);
      if (Math.abs(imp) <= 0.01) continue;
      if (!byUuid[uuidPago]) {
        byUuid[uuidPago] = {
          uuid_pago: uuidPago,
          fecha_pago: String(raw.fecha_pago || "").slice(0, 10),
          folio: raw.folio || "",
          imp_pagado: imp,
        };
      } else {
        byUuid[uuidPago].imp_pagado = Math.round((byUuid[uuidPago].imp_pagado + imp) * 100) / 100;
      }
    }
    vigentes = Object.values(byUuid).sort((a, b) =>
      String(a.fecha_pago || "").localeCompare(String(b.fecha_pago || ""))
      || String(a.uuid_pago || "").localeCompare(String(b.uuid_pago || "")),
    );
  }
  if (vigentes.length < 2) return "";
  const n = vigentes.length;
  const intro = r.posible_duplicado_complementos
    ? `El SAT tiene ${n} complementos tipo P vigentes aplicados a esta factura en el período. Si uno se emitió por error (duplicado), eso explica la diferencia de monto.`
    : `El SAT tiene ${n} complementos tipo P vigentes aplicados a esta factura en el período. Revise si todos corresponden a cobros reales o si alguno es duplicado.`;
  const lines = [
    "",
    `Posible causa — ${n} complementos de pago para la misma factura (sugerencia):`,
    intro,
    ...vigentes.map((c, i) => {
      const folio = c.folio ? ` · Folio ${c.folio}` : "";
      return `${i + 1}. UUID complemento: ${c.uuid_pago || ""}${folio} · Fecha pago ${c.fecha_pago || "—"} · Imp. pagado ${fmtMoney(c.imp_pagado)}`;
    }),
    "Acción sugerida: identifique cuál complemento refleja el cobro correcto y cancele en el SAT el que esté equivocado; luego vuelva a confrontar.",
  ];
  return `\n${lines.join("\n")}`;
}
function buildObsFuera(r) {
  if (r.emparejado_por_factura_relacionada) {
    return {
      titulo: "Emparejado por factura relacionada",
      detalle: r.nota_emparejado || (
        "El UUID del Excel corresponde a la factura relacionada (IdDocumento) o al complemento de pago, " +
        "pero la fecha de pago del SAT cae en otro mes. No es un CFDI faltante."
      ),
      color: "slate",
    };
  }
  return {
    titulo: "Fuera del período del Excel (Contabilidad)",
    detalle: `El Excel (Contabilidad) registra el cobro en ${r.fecha_excel}, pero en el sistema (SAT) el CFDI tiene fecha ${r.fecha_sistema}. ` +
             `Este pago está fuera de la fecha que contiene el Excel (Contabilidad).\n\n` +
             `Acción sugerida: revise si el cobro pertenece a otro mes y corrija la fecha de ingreso/pago en el Excel cuando esté mal capturada.`,
    color: "rose",
  };
}
function buildObsSoloExcel(r) {
  const tieneUuid = !!r.uuid;
  const mp = (r.metodo_pago || "").toUpperCase();
  
  if (r.justificacion === "Cancelado_local" || r.justificacion === "Cancelado en SAT") {
    const origen = r.cancel_source
      ? ({
          folder: "carpeta de carga (ruta con carpeta de cancelados)",
          sat_metadata: "metadatos SAT (.txt)",
          sat_verify: "verificación SAT previa",
          manual: "marcado manual en el visor",
        }[r.cancel_source] || r.cancel_source)
      : "copia local (origen no registrado)";
    const verif = r.verificado_sat_at
      ? `Última verificación SAT: ${String(r.verificado_sat_at).replace("T", " ").slice(0, 16)}.`
      : "Aún no se ha verificado contra el portal SAT en esta instalación.";
    return {
      titulo: "Marcado como cancelado en la copia local",
      detalle: `Este UUID (${r.uuid}) aparece en tu Excel (Contabilidad), pero en Visor Fiscal está como Cancelado en la base local — no necesariamente cancelado en el portal SAT.\n\n` +
               `Importe en Excel (Contabilidad): ${fmtMoney(r.total_excel)}\n` +
               `Estatus en copia local: Cancelado\n` +
               `Origen del estatus local: ${origen}\n` +
               `${verif}\n\n` +
               `Acción sugerida: abra el detalle del CFDI y pulse Verificar en SAT. Si el SAT responde Vigente, el sistema corregirá el estatus local. Si realmente está cancelado, elimine la fila del Excel o marque $0.00.`,
      color: "rose",
    };
  }

  if (!tieneUuid) {
    return {
      titulo: "Venta en efectivo (sin UUID)",
      detalle: `El Excel (Contabilidad) registra esta fila como venta en efectivo (${r.fecha_excel} · ${r.cliente}). No hay folio fiscal asociado.\n\n` +
               `Acción sugerida: confirme que realmente sea una operación sin CFDI; si existe comprobante, capture su UUID para que pueda confrontarse.`,
      color: "slate",
    };
  }
  if (excelRegistroEsComplementoPago(r) || mp.includes("PPD") || (r.fuente || "").toUpperCase().includes("PPD")) {
    return {
      titulo: "Complemento de Pago faltante en sistema (SAT)",
      detalle: `El Excel (Contabilidad) registra el complemento de pago con UUID ${r.uuid} (cobrado el ${r.fecha_excel} por ${fmtMoney(r.total_excel)}) ` +
               `pero NO hay un CFDI tipo P cargado en el sistema (SAT).\n\n` +
               `Acción sugerida: descargue ese complemento tipo P desde el SAT y cargue su XML en Visor Fiscal.`,
      color: "red",
    };
  }
  return {
    titulo: "PUE faltante en sistema (SAT)",
    detalle: `El Excel (Contabilidad) registra una factura PUE con UUID ${r.uuid} (${r.fecha_excel} · ${fmtMoney(r.total_excel)}) ` +
             `pero NO hay un CFDI tipo I cargado en el sistema (SAT).\n\n` +
             `Acción sugerida: descargue esa factura tipo I desde el SAT y cargue su XML en Visor Fiscal.`,
    color: "red",
  };
}
function buildObsSoloSistema(r) {
  const fuente = (r.fuente || "").toUpperCase();
  const tipo = fuente === "PPD" ? "Complemento de Pago" : "CFDI PUE Ingreso";
  return {
    titulo: `${tipo} faltante en Excel (Contabilidad)`,
    detalle: `El sistema (SAT) tiene este ${tipo} (UUID ${r.uuid}, fecha ${r.fecha_sistema}, total ${fmtMoney(r.total)}) ` +
             `pero NO está registrado en el Excel (Contabilidad) del control de IVA.\n\nAcción sugerida: ` +
             (fuente === "PPD"
               ? `agregue en el Excel el cobro del período y el UUID del complemento correspondiente.`
               : `agregue esta factura PUE en el Excel para que cuadre con la información fiscal.`),
    color: "rose",
  };
}
function RetencionesInformativasAviso({ rows }) {
  const info = (rows || []).filter((r) => r && r.retenciones_informativas);
  if (!info.length) return null;
  const sumaRet = info.reduce((a, r) => a + (Number(r.retenciones_total) || 0), 0);
  const sumaLoc = info.reduce((a, r) => a + (Number(r.traslados_locales_sat) || 0), 0);
  return (
    <div
      className="mb-3 rounded-sm border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-950"
      data-testid="confronta-retenciones-informativas"
    >
      <b>Retenciones e impuestos locales (informativo).</b> En {info.length} CFDI el subtotal y el IVA
      cuadran. El total del SAT ya es Subtotal − Descuento + Traslados − Retenciones; el Excel suele traer
      solo Subtotal+IVA. No se marcan como diferencia.
      {Math.abs(sumaRet) > 0.01 ? <> Retenciones: <b>{fmtMoney(sumaRet)}</b>.</> : null}
      {Math.abs(sumaLoc) > 0.01 ? <> Impuestos locales (p. ej. ISH): <b>{fmtMoney(sumaLoc)}</b>.</> : null}
    </div>
  );
}

function textoRetencionesInformativas(r) {
  if (!r?.retenciones_informativas && !r?.nota_retenciones) return "";
  const partes = [];
  if (Number(r.isr_retenido_sat) > 0.01) partes.push(`ISR retenido ${fmtMoney(r.isr_retenido_sat)}`);
  if (Number(r.iva_retenido_sat) > 0.01) partes.push(`IVA retenido ${fmtMoney(r.iva_retenido_sat)}`);
  if (Number(r.retenciones_locales_sat) > 0.01) partes.push(`retenciones locales ${fmtMoney(r.retenciones_locales_sat)}`);
  if (Number(r.retenciones_inferidas_sat) > 0.01) partes.push(`retenciones ${fmtMoney(r.retenciones_inferidas_sat)}`);
  if (Number(r.traslados_locales_sat) > 0.01) {
    const nombres = (r.impuestos_locales || [])
      .filter((x) => x && x.tipo === "traslado" && x.nombre)
      .map((x) => x.nombre);
    const etiqueta = nombres.length ? nombres.join(", ") : "impuesto local";
    partes.push(`${etiqueta} ${fmtMoney(r.traslados_locales_sat)}`);
  }
  const lista = partes.length ? partes.join("; ") : "";
  return (
    `\n\nRetenciones e impuestos locales (informativo, no es diferencia): ` +
    (lista ? `${lista}. ` : "") +
    (r.nota_retenciones || "El total del SAT ya aplica Subtotal − Descuento + Traslados − Retenciones. El Excel compara Subtotal+IVA.")
  );
}

function buildObsCoincide(r) {
  const extra = textoRetencionesInformativas(r);
  const desc = r.nota_descuento ? `\n\n${r.nota_descuento}` : "";
  return {
    titulo: r.retenciones_informativas
      ? "CFDI coincide (retenciones informativas)"
      : "CFDI coincide en ambos lados",
    detalle: `Este CFDI (UUID ${r.uuid}) está en el sistema (SAT) y en el Excel (Contabilidad) con Subtotal ${fmtMoney(r.subtotal_sistema)} e IVA ${fmtMoney(r.iva_sistema)} alineados. Total SAT ${fmtMoney(r.total_sistema)}; total Excel ${fmtMoney(r.total_excel)}.${desc}${extra}`,
    color: "emerald",
  };
}

function ConfrontaObservacionesResumen({ items, onOpenCfdi, activeClientId }) {
  if (!items.length) return null;
  const colorMap = {
    amber: "border-amber-300 bg-amber-50 text-amber-950",
    rose: "border-rose-300 bg-rose-50 text-rose-950",
    red: "border-red-300 bg-red-50 text-red-950",
    slate: "border-slate-300 bg-slate-50 text-slate-900",
  };
  return (
    <section
      className="rounded-sm border border-[#0F2D5C]/25 bg-slate-50 p-3 space-y-2"
      data-testid="confronta-observaciones-resumen"
    >
      <div>
        <h4 className="m-0 text-sm font-semibold text-[#0F2D5C]">
          Motivo y corrección por comprobante
        </h4>
        <p className="m-0 mt-0.5 text-[11px] text-slate-600">
          Identifica la factura o complemento afectado, explica por qué no cuadra y propone cómo corregirlo.
        </p>
      </div>
      <div className="space-y-2">
        {items.map((entry, index) => {
          const { row, observation, documento, uuidLabel, complementoUuid } = entry;
          const uuid = row.uuid || "";
          const folio = row.folio || row.folio_factura || "—";
          const cliente = row.cliente || row.nombre_receptor || "—";
          const fecha = row.fecha_sistema || row.fecha_excel || row.fecha || "—";
          const compsVigentes = Array.isArray(row.complementos_pago_vigentes)
            ? row.complementos_pago_vigentes
            : [];
          const alternateClientId =
            row.client_id && activeClientId && row.client_id !== activeClientId
              ? row.client_id
              : undefined;
          return (
            <details
              key={`${entry.categoria}-${uuid}-${index}`}
              open={items.length === 1}
              className={`rounded-sm border px-3 py-2 ${colorMap[observation.color] || colorMap.slate}`}
              data-testid={`confronta-observacion-resumen-${index}`}
            >
              <summary className="cursor-pointer select-none text-xs">
                <strong>{documento}</strong>
                {folio !== "—" ? <> · Folio {folio}</> : null}
                {uuid ? <> · {uuid.slice(0, 8)}…</> : null}
                {" — "}
                <span className="font-semibold">{observation.titulo}</span>
              </summary>
              <div className="mt-2 space-y-2">
                <div className="grid gap-1 text-[10px] text-slate-700 sm:grid-cols-2 xl:grid-cols-4">
                  <span><strong>{uuidLabel || "UUID factura"}:</strong> <span className="font-mono break-all">{uuid || "—"}</span></span>
                  {compsVigentes.length >= 2 ? (
                    <span className="sm:col-span-2 xl:col-span-3">
                      <strong>{compsVigentes.length} UUID complemento:</strong>{" "}
                      <span className="font-mono break-all">
                        {compsVigentes.map((c) => c.uuid_pago).filter(Boolean).join(" · ")}
                      </span>
                    </span>
                  ) : complementoUuid ? (
                    <span><strong>UUID complemento:</strong> <span className="font-mono break-all">{complementoUuid}</span></span>
                  ) : null}
                  <span><strong>Fecha:</strong> {fecha}</span>
                  <span><strong>Cliente:</strong> {cliente}</span>
                </div>
                <div className="whitespace-pre-line text-[11px] leading-relaxed">
                  {observation.detalle}
                </div>
                {uuid && onOpenCfdi ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-sm border border-current/30 bg-white px-2 py-1 text-[10px] font-semibold hover:bg-white/60"
                    onClick={() => onOpenCfdi(uuid, observation, alternateClientId)}
                  >
                    <Eye className="h-3 w-3" />
                    Abrir CFDI completo
                  </button>
                ) : null}
              </div>
            </details>
          );
        })}
      </div>
    </section>
  );
}

function DiferenciasReport({ data: rawData, onOpenCfdi, onOpenSatVisor, activeClientId }) {
  const data = normalizeConfrontaResponse(rawData, "cobrados");
  const esCanceladoLocal = (e) =>
    e?.justificacion === "Cancelado_local" || e?.justificacion === "Cancelado en SAT";
  const pueFaltaExcel = data.solo_en_sistema.filter(s => s.fuente === "PUE");
  const ppFaltaExcel  = data.solo_en_sistema.filter(s => s.fuente === "PPD");
  // Cancelados no son «faltantes activos»: van en solo Excel con estatus CANCELADO (local).
  const pueFaltaSis   = data.solo_en_excel.filter(
    (e) => e.uuid && !esCanceladoLocal(e) && !excelRegistroEsComplementoPago(e) && (e.metodo_pago || "").toUpperCase().includes("PUE"),
  );
  const ppFaltaSis    = data.solo_en_excel.filter(
    (e) => e.uuid && !esCanceladoLocal(e) && excelRegistroEsComplementoPago(e),
  );
  const otrosFaltaSis = data.solo_en_excel.filter(
    (e) => e.uuid && !esCanceladoLocal(e) && !excelRegistroEsComplementoPago(e) && !(e.metodo_pago || "").toUpperCase().includes("PUE"),
  );
  const canceladosExcel = data.solo_en_excel.filter(esCanceladoLocal);
  const dif           = data.mismatched || [];
  const fueraPeriodo  = data.fuera_de_periodo || [];

  const sumKey = (rows, k) => rows.reduce((a,r) => a + (r[k] || 0), 0);
  const aporteIva =
      sumKey(pueFaltaExcel, "iva") + sumKey(ppFaltaExcel,  "iva")
    - sumKey(pueFaltaSis,   "iva_excel") - sumKey(ppFaltaSis, "iva_excel")
    - sumKey(otrosFaltaSis, "iva_excel")
    - sumKey(fueraPeriodo,  "iva_excel") + sumKey(dif, "diff_iva");

  const obsForSoloSis = (r) => buildObsSoloSistema(r);
  const obsForSoloExcel = (r) => buildObsSoloExcel(r);
  const obsForDif = (r) => buildObsDiferencia(r);
  const obsForFuera = (r) => buildObsFuera(r);
  const observacionesResumen = [
    ...dif.map((row) => {
      const esPpd = row.fuente === "PPD" || String(row.metodo_pago || "").toUpperCase().includes("PPD");
      return {
        categoria: "monto",
        row,
        documento: esPpd ? "Factura PPD / complemento de pago" : "Factura",
        uuidLabel: "UUID factura",
        complementoUuid: row.uuid_pago || "",
        observation: obsForDif(row),
      };
    }),
    ...ppFaltaExcel.map((row) => ({
      categoria: "ppd_solo_sistema",
      row,
      documento: "Factura PPD cobrada por complemento",
      uuidLabel: "UUID factura",
      complementoUuid: row.uuid_pago || "",
      observation: obsForSoloSis(row),
    })),
    ...pueFaltaExcel.map((row) => ({
      categoria: "pue_solo_sistema",
      row,
      documento: "Factura PUE",
      uuidLabel: "UUID factura",
      observation: obsForSoloSis(row),
    })),
    ...ppFaltaSis.map((row) => ({
      categoria: "ppd_solo_excel",
      row,
      documento: "Complemento de pago",
      uuidLabel: "UUID complemento",
      observation: obsForSoloExcel(row),
    })),
    ...pueFaltaSis.map((row) => ({
      categoria: "pue_solo_excel",
      row,
      documento: "Factura PUE",
      uuidLabel: "UUID factura",
      observation: obsForSoloExcel(row),
    })),
    ...otrosFaltaSis.map((row) => ({
      categoria: "otro_solo_excel",
      row,
      documento: "CFDI registrado en contabilidad",
      uuidLabel: "UUID CFDI",
      observation: obsForSoloExcel(row),
    })),
    ...canceladosExcel.map((row) => ({
      categoria: "cancelado_local",
      row,
      documento: "CFDI marcado como cancelado",
      uuidLabel: "UUID CFDI",
      observation: obsForSoloExcel(row),
    })),
    ...fueraPeriodo.map((row) => ({
      categoria: "fuera_periodo",
      row,
      documento: "CFDI fuera de período",
      uuidLabel: "UUID factura",
      complementoUuid: row.uuid_pago || "",
      observation: obsForFuera(row),
    })),
  ];

  const excelEnPeriodo =
    data.excel_meta?.rows_en_periodo ?? data.totales?.excel?.count ?? null;
  const avisoSinExcel =
    excelEnPeriodo === 0 &&
    (pueFaltaExcel.length > 0 || ppFaltaExcel.length > 0 || (data.summary?.solo_en_sistema || 0) > 0);
  const sinDiferencias = !confrontaHasDifferences(data);

  return (
    <div className="space-y-4">
      {sinDiferencias ? (
        <div
          className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-xs text-emerald-950"
          data-testid="confronta-sin-diferencias-detalle"
        >
          <b>No hay diferencias en este período.</b> Los importes del sistema (SAT) y de la contabilidad (Excel,
          CSV o conciliación manual) están alineados para {data.fecha_desde} → {data.fecha_hasta}.
        </div>
      ) : null}
      {!sinDiferencias ? (
      <>
      <ConfrontaObservacionesResumen
        items={observacionesResumen}
        onOpenCfdi={onOpenCfdi}
        activeClientId={activeClientId}
      />
      <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900">
        <b>Reporte de diferencias para corrección.</b> Toma como base el sistema (SAT) (CFDI cargados desde XML) y muestra qué falta o no coincide en la contabilidad (Excel, CSV o conciliación manual) del período {data.fecha_desde} → {data.fecha_hasta}. El bloque anterior identifica el comprobante, explica el motivo y presenta la corrección sugerida.
      </div>
      {avisoSinExcel && (
        <div
          className="bg-red-50 border border-red-200 rounded-sm p-3 text-xs text-red-950"
          data-testid="confronta-sin-excel-periodo"
        >
          <b>Sin filas de contabilidad en este período.</b> No hay registros (Excel, CSV ni conciliación manual) con
          fecha de ingreso o documento entre {data.fecha_desde} y {data.fecha_hasta}. Si el control del mes sí tiene datos,
          vuelva a cargar el archivo en{" "}
          <a href="/carga-informacion" className="underline font-semibold">
            Carga de información
          </a>{" "}
          y revise que la fecha de cobro/ingreso del Excel sea la del mes confrontado.
        </div>
      )}

      <DifSection title="PUE faltantes en Excel (Contabilidad)" subtitle="Sistema (SAT) tiene la PUE pero el Excel (Contabilidad) no la registra"
        rows={pueFaltaExcel} color="rose" onOpenCfdi={onOpenCfdi} onOpenSatVisor={onOpenSatVisor} activeClientId={activeClientId} obsBuilder={obsForSoloSis}
        cols={[
          {k:"uuid", label:"UUID", mono:true, render:(r)=>r.uuid?.slice(0,8)+"…"},
          {k:"fcfdi", label: "Fecha CFDI (SAT)", render:(r)=>r.fecha_sistema},
          {k:"folio", label:"Folio", render:(r)=>r.folio},
          {k:"cliente", label:"Cliente", render:(r)=>r.cliente || r.nombre_receptor},
          {k:"sub", label:"Subtotal", right:true, render:(r)=>fmtMoney(r.subtotal)},
          {k:"iva", label:"IVA", right:true, render:(r)=><span className="text-rose-700 font-semibold">{fmtMoney(r.iva)}</span>},
          {k:"tot", label:"Total", right:true, render:(r)=>fmtMoney(r.total)},
        ]}
        totals={{ sub: sumKey(pueFaltaExcel,"subtotal"), iva: sumKey(pueFaltaExcel,"iva"), tot: sumKey(pueFaltaExcel,"total") }}/>

      <DifSection title="PUE faltantes en sistema (SAT)" subtitle="Excel (Contabilidad) registra una PUE pero no hay CFDI cargado en el sistema (SAT)"
        rows={pueFaltaSis} color="red" onOpenCfdi={onOpenCfdi} onOpenSatVisor={onOpenSatVisor} activeClientId={activeClientId} obsBuilder={obsForSoloExcel}
        cols={[
          {k:"uuid", label:"UUID", mono:true, render:(r)=> r.uuid ? (r.uuid.slice(0,8)+"…") : <span className="text-slate-400">(sin UUID)</span>},
          {k:"fexcel", label: CF.FEC_EXCEL_CONTAB, render:(r)=>r.fecha_excel},
          {k:"folio", label:"Folio", render:(r)=>r.folio},
          {k:"cliente", label:"Cliente", render:(r)=>r.cliente},
          {k:"sub", label:"Subtotal", right:true, render:(r)=>fmtMoney(r.subtotal_excel)},
          {k:"iva", label:"IVA", right:true, render:(r)=><span className="text-red-700 font-semibold">{fmtMoney(r.iva_excel)}</span>},
          {k:"tot", label:"Total", right:true, render:(r)=>fmtMoney(r.total_excel)},
        ]}
        totals={{ sub: sumKey(pueFaltaSis,"subtotal_excel"), iva: sumKey(pueFaltaSis,"iva_excel"), tot: sumKey(pueFaltaSis,"total_excel") }}/>

      <DifSection title="Complementos de pago faltantes en Excel (Contabilidad)" subtitle="Sistema (SAT) tiene el complemento pero el Excel (Contabilidad) no lo registra"
        rows={ppFaltaExcel} color="rose" onOpenCfdi={onOpenCfdi} onOpenSatVisor={onOpenSatVisor} activeClientId={activeClientId} obsBuilder={obsForSoloSis}
        cols={[
          {k:"uuid", label:"UUID", mono:true, render:(r)=>r.uuid?.slice(0,8)+"…"},
          {k:"fcfdi", label:"Fecha cobro (SAT)", render:(r)=>r.fecha_sistema},
          {k:"folio", label:"Folio", render:(r)=>r.folio},
          {k:"cliente", label:"Cliente", render:(r)=>r.cliente || r.nombre_receptor},
          {k:"sub", label:"Subtotal", right:true, render:(r)=>fmtMoney(r.subtotal)},
          {k:"iva", label:"IVA", right:true, render:(r)=><span className="text-rose-700 font-semibold">{fmtMoney(r.iva)}</span>},
          {k:"tot", label:"Total", right:true, render:(r)=>fmtMoney(r.total)},
        ]}
        totals={{ sub: sumKey(ppFaltaExcel,"subtotal"), iva: sumKey(ppFaltaExcel,"iva"), tot: sumKey(ppFaltaExcel,"total") }}/>

      <DifSection title="Complementos de pago faltantes en sistema (SAT)" subtitle="Excel (Contabilidad) registra el complemento pero no hay CFDI P cargado en sistema (SAT)"
        rows={ppFaltaSis} color="red" onOpenCfdi={onOpenCfdi} onOpenSatVisor={onOpenSatVisor} activeClientId={activeClientId} obsBuilder={obsForSoloExcel}
        cols={[
          {k:"uuid", label:"UUID", mono:true, render:(r)=> r.uuid ? (r.uuid.slice(0,8)+"…") : <span className="text-slate-400">(sin UUID)</span>},
          {k:"fexcel", label: CF.FEC_EXCEL_CONTAB, render:(r)=>r.fecha_excel},
          {k:"folio", label:"Folio", render:(r)=>r.folio},
          {k:"cliente", label:"Cliente", render:(r)=>r.cliente},
          {k:"sub", label:"Subtotal", right:true, render:(r)=>fmtMoney(r.subtotal_excel)},
          {k:"iva", label:"IVA", right:true, render:(r)=><span className="text-red-700 font-semibold">{fmtMoney(r.iva_excel)}</span>},
          {k:"tot", label:"Total", right:true, render:(r)=>fmtMoney(r.total_excel)},
        ]}
        totals={{ sub: sumKey(ppFaltaSis,"subtotal_excel"), iva: sumKey(ppFaltaSis,"iva_excel"), tot: sumKey(ppFaltaSis,"total_excel") }}/>

      <DifSection title="Con diferencia de monto" subtitle="Mismo UUID en sistema (SAT) y Excel (Contabilidad) pero importes distintos"
        rows={dif} color="amber" onOpenCfdi={onOpenCfdi} onOpenSatVisor={onOpenSatVisor} activeClientId={activeClientId} obsBuilder={obsForDif}
        cols={[
          {k:"uuid", label:"UUID", mono:true, render:(r)=>r.uuid?.slice(0,8)+"…"},
          {k:"fcfdi", label: "Fecha CFDI (SAT)", render:(r)=>r.fecha_sistema},
          {k:"fexcel", label: CF.FEC_EXCEL_CONTAB, render:(r)=>r.fecha_excel},
          {k:"folio", label:"Folio", render:(r)=>r.folio},
          {k:"cliente", label:"Cliente", render:(r)=>r.cliente},
          {k:"diff_sub", label:"Δ Subtotal", right:true, render:(r)=>fmtMoney(r.diff_subtotal)},
          {k:"diff_iva", label:"Δ IVA", right:true, render:(r)=><span className="text-amber-700 font-semibold">{fmtMoney(r.diff_iva)}</span>},
          {k:"diff_tot", label:"Δ Total", right:true, render:(r)=>fmtMoney(r.diff_total)},
        ]}
        totals={{ sub: sumKey(dif,"diff_subtotal"), iva: sumKey(dif,"diff_iva"), tot: sumKey(dif,"diff_total") }}/>

      <DifSection title="Fuera de período" subtitle="Excel (Contabilidad) registra el cobro en este período, pero el sistema (SAT) lo tiene en otra fecha."
        rows={fueraPeriodo} color="rose" onOpenCfdi={onOpenCfdi} onOpenSatVisor={onOpenSatVisor} activeClientId={activeClientId} obsBuilder={obsForFuera}
        cols={[
          {k:"uuid", label:"UUID", mono:true, render:(r)=>r.uuid?.slice(0,8)+"…"},
          {k:"fcfdi", label: CF.FEC_SISTEMA_REAL_SAT, render:(r)=><span className="text-rose-700 font-semibold">{r.fecha_sistema}</span>},
          {k:"fexcel", label: CF.FEC_EXCEL_CONTAB, render:(r)=>r.fecha_excel},
          {k:"folio", label:"Folio", render:(r)=>r.folio},
              {k:"cliente", label:"Cliente", render:(r)=>r.cliente},
              {k:"nota", label:"Nota", render:(r)=> r.emparejado_por_factura_relacionada
                ? <span className="text-sky-800 font-semibold">Emparejado por factura relacionada</span>
                : <span className="text-slate-400">Otro mes</span>},
              {k:"sub", label:"Subtotal", right:true, render:(r)=>fmtMoney(r.subtotal_excel)},
              {k:"iva", label:"IVA", right:true, render:(r)=>fmtMoney(r.iva_excel)},
              {k:"diff", label:"Diff IVA", right:true, render:(r)=><span className="text-rose-700 font-semibold">{fmtMoney(-r.iva_excel)}</span>},
            ]}
        totals={{ sub: -sumKey(fueraPeriodo,"subtotal_excel"), iva: -sumKey(fueraPeriodo,"iva_excel"), tot: -sumKey(fueraPeriodo,"total_excel") }}/>

      {(data.excel_meta?.excluidas_sin_folio_fiscal ?? 0) > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs text-slate-700" data-testid="confronta-excluidas-sin-uuid">
          <b>{fmtNumber(data.excel_meta.excluidas_sin_folio_fiscal)} fila(s) de Excel (Contabilidad) sin UUID SAT válido</b> quedaron
          fuera del cruce (transferencias, folio numérico, conceptos sin CFDI, etc.). Siguen en la carga contable pero no marcan diferencia con el SAT.
        </div>
      )}

      <div className="bg-slate-900 text-white rounded-sm px-4 py-2.5">
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold leading-tight max-w-[15rem] shrink-0">
            {CF.DIF_TOTAL_BLOQUE}
          </div>
          <div className="grid grid-cols-3 gap-6 sm:gap-10 text-sm min-w-0">
            <div>
              <div className="text-[10px] text-slate-400 leading-none">Subtotal</div>
              <div className="num text-base font-bold leading-tight mt-0.5">{fmtMoney(data.totales.diff.subtotal)}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-400 leading-none">IVA</div>
              <div className="num text-base font-bold leading-tight mt-0.5">{fmtMoney(data.totales.diff.iva)}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-400 leading-none">Total</div>
              <div className="num text-base font-bold leading-tight mt-0.5">{fmtMoney(data.totales.diff.total)}</div>
            </div>
          </div>
        </div>
        <div className="mt-2 pt-2 border-t border-slate-700 text-[10px] text-slate-400 leading-snug">
          Validación: suma de aportes IVA por sección = <span className={`font-semibold ${Math.abs(aporteIva - data.totales.diff.iva) < 0.05 ? "text-emerald-300" : "text-rose-300"}`}>{fmtMoney(aporteIva)}</span>
          {Math.abs(aporteIva - data.totales.diff.iva) < 0.05 ? " ✓ coincide con Diff IVA" : " ⚠ no coincide — diferencia: " + fmtMoney(aporteIva - data.totales.diff.iva)}
        </div>
      </div>
      </>
      ) : null}
    </div>
  );
}

function DifSection({ title, subtitle, rows, cols, totals, color, onOpenCfdi, obsBuilder, onOpenSatVisor, activeClientId }) {
  const colorMap = {
    rose:  "border-rose-200  bg-rose-50",
    red:   "border-red-200   bg-red-50",
    amber: "border-amber-200 bg-amber-50",
    slate: "border-slate-200 bg-slate-50",
  };
  if (!rows.length) return null;
  return (
    <div className={`border rounded-sm overflow-hidden ${colorMap[color]}`} data-testid={`dif-section-${title.toLowerCase().replace(/ /g,'-')}`}>
      <div className="px-4 py-2 border-b border-slate-200 bg-white">
        <div className="text-sm font-semibold text-slate-900" style={{fontFamily:"'Chivo'"}}>{title} <span className="text-slate-400 font-normal">({rows.length})</span></div>
        <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>
      </div>
      <div className="bg-white max-h-72 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 sticky top-0">
            <tr>
              {cols.map(c => (
                <th key={c.k} className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-500 ${c.right ? "text-right" : "text-left"}`}>{c.label}</th>
              ))}
              <th className="w-36 text-right text-[10px] uppercase tracking-wider font-semibold text-slate-500">Acc.</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0,200).map((r,i) => {
              const observation = obsBuilder ? obsBuilder(r) : null;
              const openRow = () => {
                if (!r.uuid) return;
                const alt = r.client_id && activeClientId && r.client_id !== activeClientId ? r.client_id : undefined;
                onOpenCfdi(r.uuid, observation, alt);
              };
              return (
                <tr
                  key={r.uuid || i}
                  onClick={openRow}
                  className={`border-t border-slate-100 ${r.uuid ? "cursor-pointer hover:bg-slate-50" : ""}`}>
                  {cols.map(c => (
                    <td key={c.k} className={`px-3 py-1 ${c.right ? "text-right num" : ""} ${c.mono ? "font-mono text-[10px] text-slate-700" : "text-slate-700"}`}>{c.render(r)}</td>
                  ))}
                  <td className="px-2 py-1" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      {r.uuid && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 rounded-sm border border-slate-200 bg-white px-1.5 py-1 text-[10px] font-semibold text-slate-700 hover:bg-slate-100"
                          title="Ver explicación y CFDI completo"
                          onClick={openRow}
                        >
                          <Eye className="w-3 h-3" />
                          Ver explicación
                        </button>
                      )}
                      {r.uuid && onOpenSatVisor && (
                        <button
                          type="button"
                          className="p-1 rounded-sm text-[#0F2D5C] hover:bg-slate-100"
                          title="Ver CFDI y PDF aquí"
                          onClick={() => {
                            const alt = r.client_id && activeClientId && r.client_id !== activeClientId ? r.client_id : undefined;
                            onOpenSatVisor(r.uuid, alt);
                          }}
                        >
                          <FileSearch className="w-3.5 h-3.5"/>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-slate-100 sticky bottom-0">
            <tr className="border-t-2 border-slate-300 text-xs">
              <td colSpan={Math.max(0, cols.length - 3)} className="px-3 py-1.5 text-right text-[10px] uppercase tracking-wider font-semibold text-slate-700">Total</td>
              <td className="px-3 py-1.5 text-right num font-bold">{fmtMoney(totals.sub)}</td>
              <td className="px-3 py-1.5 text-right num font-bold">{fmtMoney(totals.iva)}</td>
              <td className="px-3 py-1.5 text-right num font-bold">{fmtMoney(totals.tot)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
        {rows.length > 200 && <div className="px-3 py-2 text-[11px] text-slate-500 bg-slate-50 border-t border-slate-200 text-center">Mostrando 200 de {rows.length}. Descarga el reporte para ver todos.</div>}
      </div>
    </div>
  );
}

function DetailTable({ rows, cols, onOpenCfdi, obsBuilder, onOpenSatVisor, activeClientId }) {
  if (!rows?.length) return <div className="py-10 text-center text-slate-500 text-sm">Sin registros.</div>;
  return (
    <div className="border border-slate-200 rounded-sm overflow-hidden mt-2 max-h-[480px] overflow-y-auto">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 sticky top-0 z-10">
          <tr>
            {cols.map(c => (
              <th key={c.k} className={`px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 ${c.right ? "text-right" : "text-left"}`}>
                {c.label}
              </th>
            ))}
            <th className="w-36 text-right text-[10px] uppercase tracking-wider font-semibold text-slate-500">Acc.</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 300).map((r, i) => {
            const observation = obsBuilder ? obsBuilder(r) : null;
            const openRow = () => {
              if (!r.uuid) return;
              const alt = r.client_id && activeClientId && r.client_id !== activeClientId ? r.client_id : undefined;
              onOpenCfdi && onOpenCfdi(r.uuid, observation, alt);
            };
            return (
            <tr key={r.uuid || i}
                onClick={openRow}
                className={`border-t border-slate-100 ${r.uuid ? "cursor-pointer hover:bg-slate-50" : ""}`}>
              {cols.map(c => (
                <td key={c.k} className={`px-3 py-1.5 ${c.right ? "text-right num" : ""} ${c.mono ? "font-mono text-[10px] text-slate-700" : "text-slate-700"}`}>
                  {c.render(r)}
                </td>
              ))}
              <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-end gap-1">
                  {r.uuid && (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 rounded-sm border border-slate-200 bg-white px-1.5 py-1 text-[10px] font-semibold text-slate-700 hover:bg-slate-100 whitespace-nowrap"
                      title="Ver explicación y CFDI completo"
                      onClick={openRow}
                    >
                      <Eye className="w-3 h-3" />
                      Ver explicación
                    </button>
                  )}
                  {r.uuid && onOpenSatVisor && (
                    <button
                      type="button"
                      className="p-0.5 rounded-sm text-[#0F2D5C] hover:bg-slate-100"
                      title="Ver CFDI y PDF aquí"
                      onClick={() => {
                        const alt = r.client_id && activeClientId && r.client_id !== activeClientId ? r.client_id : undefined;
                        onOpenSatVisor(r.uuid, alt);
                      }}
                    >
                      <FileSearch className="w-3.5 h-3.5"/>
                    </button>
                  )}
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
      {rows.length > 300 && <div className="px-3 py-2 text-[11px] text-slate-500 bg-slate-50 border-t border-slate-200 text-center">Mostrando 300 de {rows.length}. Descarga el reporte para ver todos.</div>}
    </div>
  );
}
